import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { CURRENCIES } from "@/lib/countries";
import axios from "axios";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Trophy, BarChart3, FileText, Package, ShoppingCart, Settings,
  LogOut, Plus, Edit, Trash2, Check, X, Eye, DollarSign,
  Layers, Calendar as CalendarIcon, Minus, CheckCircle, Upload, Search, PieChart, Headphones, MessageSquare, Send, Pencil, Zap
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const statusClass = (s) => `status-${s}`;

const PromotionsCard = () => {
  const [promo, setPromo] = useState({ site_discount_pct: 0, site_discount_label: "", bogo_enabled: false, bogo_min_packages: 3 });
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_pct: 10, description: "", usage_limit: "" });
  useEffect(() => {
    axios.get(`${API}/promotions`).then(r => setPromo({ ...promo, ...r.data })).catch(() => {});
    axios.get(`${API}/admin/coupons`, { withCredentials: true }).then(r => setCoupons(r.data)).catch(() => {});
  }, []);
  const savePromo = async () => {
    try {
      await axios.post(`${API}/admin/promotions`, promo, { withCredentials: true });
      toast.success("Promotions saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const createCoupon = async () => {
    if (!newCoupon.code.trim()) { toast.error("Code required"); return; }
    try {
      const r = await axios.post(`${API}/admin/coupons`, newCoupon, { withCredentials: true });
      setCoupons([r.data, ...coupons]);
      setNewCoupon({ code: "", discount_pct: 10, description: "", usage_limit: "" });
      toast.success("Coupon created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { withCredentials: true });
      setCoupons(coupons.filter(c => c.coupon_id !== id));
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="promotions-card">
      <CardHeader>
        <CardTitle className="text-base">Promotions, Discounts & Coupons</CardTitle>
        <p className="text-xs text-gray-500 mt-1">Configure sitewide discount banner, the &quot;Buy 3 Get 1 Free&quot; promo, and admin-managed coupon codes.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Sitewide discount + BOGO */}
        <div className="grid sm:grid-cols-2 gap-4 border-b pb-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase text-gray-500">Sitewide Discount %</Label>
            <Input type="number" min="0" max="90" value={promo.site_discount_pct} onChange={e => setPromo({...promo, site_discount_pct: e.target.value})} data-testid="promo-pct" />
            <Label className="text-xs uppercase text-gray-500">Banner Label</Label>
            <Input value={promo.site_discount_label || ""} onChange={e => setPromo({...promo, site_discount_label: e.target.value})} placeholder="Limited Time Offer" data-testid="promo-label" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={promo.bogo_enabled} onChange={e => setPromo({...promo, bogo_enabled: e.target.checked})} className="h-4 w-4" data-testid="bogo-toggle" />
              <Label className="text-sm">Enable &quot;Buy {promo.bogo_min_packages} packages — Get 1 Free Random&quot; promo</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-gray-500">Min packages</Label>
              <Input type="number" min="2" max="10" value={promo.bogo_min_packages} onChange={e => setPromo({...promo, bogo_min_packages: e.target.value})} className="w-20 h-8" data-testid="bogo-min" />
            </div>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={savePromo} data-testid="save-promo-btn">Save Promotions</Button>
          </div>
        </div>

        {/* Coupons */}
        <div>
          <p className="text-sm font-bold text-gray-700 mb-2">Coupon Codes</p>
          <div className="grid sm:grid-cols-12 gap-2 mb-3">
            <Input placeholder="CODE" className="sm:col-span-2 h-9 text-sm font-mono uppercase" value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} data-testid="new-coupon-code" />
            <Input type="number" placeholder="% off" min="1" max="90" className="sm:col-span-1 h-9 text-sm" value={newCoupon.discount_pct} onChange={e => setNewCoupon({...newCoupon, discount_pct: e.target.value})} data-testid="new-coupon-pct" />
            <Input placeholder="Description (optional)" className="sm:col-span-6 h-9 text-sm" value={newCoupon.description} onChange={e => setNewCoupon({...newCoupon, description: e.target.value})} data-testid="new-coupon-desc" />
            <Input type="number" placeholder="Limit (0=∞)" min="0" className="sm:col-span-1 h-9 text-sm" value={newCoupon.usage_limit} onChange={e => setNewCoupon({...newCoupon, usage_limit: e.target.value})} data-testid="new-coupon-limit" />
            <Button size="sm" className="sm:col-span-2 bg-blue-600 hover:bg-blue-700 text-white h-9" onClick={createCoupon} data-testid="create-coupon-btn">+ Create</Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {coupons.map(c => (
              <div key={c.coupon_id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm" data-testid={`coupon-row-${c.code}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono font-bold bg-gray-900 text-amber-300 rounded px-2 py-0.5 text-xs">{c.code}</span>
                  <span className="text-emerald-700 font-bold">{c.discount_pct}% OFF</span>
                  <span className="text-gray-500 text-xs">{c.description || "—"}</span>
                  <span className="text-gray-400 text-xs">{c.times_used || 0}{c.usage_limit ? `/${c.usage_limit}` : ""} uses</span>
                </div>
                <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => deleteCoupon(c.coupon_id)} data-testid={`delete-coupon-${c.code}`}>Delete</Button>
              </div>
            ))}
            {coupons.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No coupons yet — create one above.</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const GeneratePoolFixturesButton = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => {
    const d = new Date();
    // Default to upcoming Saturday
    const dow = d.getDay();
    const add = (6 - dow + 7) % 7 || 7;
    d.setDate(d.getDate() + add);
    return d.toISOString().slice(0, 10);
  });
  const [week, setWeek] = useState(1);
  const [color, setColor] = useState("blue");
  const [clearExisting, setClearExisting] = useState(true);
  const [autoPackages, setAutoPackages] = useState(true);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/auto-generate-pool`, {
        date,
        week_number: Number(week),
        coupon_color: color,
        clear_existing: clearExisting,
        auto_generate_packages: autoPackages,
      }, { withCredentials: true });
      const count = res.data?.count || 0;
      const pkgs = res.data?.packages?.count || 0;
      toast.success(`Generated ${count} fixtures${pkgs ? ` + ${pkgs} packages` : ""}`);
      setOpen(false);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch from Ablefast. Check the date.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button
        className="bg-amber-500 hover:bg-amber-600 text-white"
        onClick={() => setOpen(true)}
        data-testid="generate-fixtures-btn"
      >
        <Zap className="h-4 w-4 mr-2" /> Generate 49 from Ablefast
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="generate-fixtures-dialog">
          <DialogHeader>
            <DialogTitle>Generate Pool Fixtures from Ablefast</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Fetches the 49 matches from <code>ablefast.com/results/&#123;date&#125;</code>, stamps each with its real coupon number, prediction (1/X/2/Over 2.5/GG), and probability scores. Optionally regenerates priced packages.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase text-gray-500">Coupon date (Saturday)</Label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="gen-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-gray-500">Week number</Label>
                <Input type="number" min={1} value={week} onChange={e => setWeek(e.target.value)} data-testid="gen-week-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-gray-500">Coupon color</Label>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger data-testid="gen-color-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="yellow">Yellow</SelectItem>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="white">White</SelectItem>
                  <SelectItem value="brown">Brown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={clearExisting} onChange={e => setClearExisting(e.target.checked)} data-testid="gen-clear-checkbox" />
                Clear existing pool first
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={autoPackages} onChange={e => setAutoPackages(e.target.checked)} data-testid="gen-packages-checkbox" />
                Also regenerate packages
              </label>
            </div>
            <Button onClick={run} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white w-full" data-testid="gen-run-btn">
              {loading ? "Generating…" : "Generate 49 fixtures from Ablefast"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const HeroBannerCard = () => {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    axios.get(`${API}/hero-banner`).then(r => setB(r.data)).catch(() => setB({}));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/hero-banner`, b, { withCredentials: true });
      toast.success("Hero banner saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setB(prev => ({ ...prev, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  if (!b) return null;
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="hero-banner-card">
      <CardHeader>
        <CardTitle className="text-base">Homepage Hero Banner</CardTitle>
        <p className="text-xs text-gray-500 mt-1">The single sitewide CTA banner above the Latest Posts section. Replaces the old hard-coded green block.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!b.enabled} onChange={e => setB({ ...b, enabled: e.target.checked })} data-testid="hero-enabled" />
          Banner visible on homepage
        </label>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Title</Label>
          <Input value={b.title || ""} onChange={e => setB({ ...b, title: e.target.value })} placeholder="This Weekend's Fixed Draws" data-testid="hero-title-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Badge (small pill above title)</Label>
          <Input value={b.badge || ""} onChange={e => setB({ ...b, badge: e.target.value })} placeholder="Weekend Draws Available" data-testid="hero-badge-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Subtitle</Label>
          <Textarea rows={2} value={b.subtitle || ""} onChange={e => setB({ ...b, subtitle: e.target.value })} placeholder="Get our expert predictions for this weekend's pool…" data-testid="hero-subtitle-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Primary button text</Label>
            <Input value={b.button_text || ""} onChange={e => setB({ ...b, button_text: e.target.value })} placeholder="Buy Fixed Draws" data-testid="hero-btn-text" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Primary button link</Label>
            <Input value={b.button_link || ""} onChange={e => setB({ ...b, button_link: e.target.value })} placeholder="/buy or https://…" data-testid="hero-btn-link" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Secondary button text (optional)</Label>
            <Input value={b.button2_text || ""} onChange={e => setB({ ...b, button2_text: e.target.value })} placeholder="I Have a Code" data-testid="hero-btn2-text" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Secondary button link</Label>
            <Input value={b.button2_link || ""} onChange={e => setB({ ...b, button2_link: e.target.value })} placeholder="/view-code" data-testid="hero-btn2-link" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Background theme</Label>
          <Select value={b.background || "green"} onValueChange={(v) => setB({ ...b, background: v })}>
            <SelectTrigger data-testid="hero-bg-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="green">Green (default)</SelectItem>
              <SelectItem value="amber">Amber / Orange</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="blue">Blue</SelectItem>
              <SelectItem value="red">Red</SelectItem>
              <SelectItem value="purple">Purple</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Background image (optional)</Label>
          <div className="flex items-center gap-3">
            <Input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="h-9 text-xs" data-testid="hero-image-input" />
            {b.image_url && <img src={b.image_url} alt="" className="h-10 w-16 object-cover rounded border" />}
          </div>
          {b.image_url && (
            <button type="button" onClick={() => setB({ ...b, image_url: "" })} className="text-[11px] text-red-500 hover:underline">Remove image</button>
          )}
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={saving} data-testid="hero-save-btn">
          {saving ? "Saving…" : "Save Hero Banner"}
        </Button>
      </CardContent>
    </Card>
  );
};

const WeekendInfoCard = () => {
  const FIELDS = [
    { key: "week_label",         label: "Week label badge",  ph: "Week 42 · Blue Coupon" },
    { key: "friday_matches",     label: "Friday matches",    ph: "Arsenal vs Chelsea 20:00 GMT" },
    { key: "early_kickoff",      label: "Early kick-off",    ph: "Sat 12:30 — Man Utd vs Liverpool" },
    { key: "late_kickoff",       label: "Late kick-off",     ph: "Sat 17:30 — Tottenham vs Newcastle" },
    { key: "sunday_matches",     label: "Sunday matches",    ph: "Sun 14:00 — Aston Villa vs West Ham\nSun 16:30 — Brighton vs Crystal Palace" },
    { key: "postponed_matches",  label: "Postponed matches", ph: "Wolves vs Leicester (re-scheduled to Wed)" },
    { key: "note",               label: "Note / disclaimer", ph: "Times shown are local. Verify before submission." },
  ];
  const [info, setInfo] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/weekend-info`).then(r => setInfo(r.data || {})).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/weekend-info`, info, { withCredentials: true });
      toast.success("Weekend pool info saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="weekend-info-card">
      <CardHeader>
        <CardTitle className="text-base">Weekend Pool Information — sitewide schedule card</CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Set the kickoff schedule shown on the homepage. Leave any field empty to hide that row. The whole card is hidden if all fields are blank.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {FIELDS.map(f => (
          <div key={f.key} className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">{f.label}</Label>
            {f.key === "sunday_matches" || f.key === "postponed_matches" || f.key === "note" || f.key === "friday_matches" ? (
              <Textarea
                rows={2}
                value={info[f.key] || ""}
                onChange={e => setInfo({ ...info, [f.key]: e.target.value })}
                placeholder={f.ph}
                data-testid={`wi-${f.key}`}
                className="text-xs"
              />
            ) : (
              <Input
                value={info[f.key] || ""}
                onChange={e => setInfo({ ...info, [f.key]: e.target.value })}
                placeholder={f.ph}
                data-testid={`wi-${f.key}`}
              />
            )}
          </div>
        ))}
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={saving} data-testid="wi-save-btn">
          {saving ? "Saving…" : "Save Weekend Info"}
        </Button>
      </CardContent>
    </Card>
  );
};

const FreePreviewContentCard = () => {
  const [content, setContent] = useState({ title: "", intro: "", long_perm_header: "", long_perm_footer: "" });
  const [placeholders, setPlaceholders] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/free-preview-content`, { withCredentials: true })
      .then(r => {
        setContent({
          title: r.data.title,
          intro: r.data.intro,
          long_perm_header: r.data.long_perm_header || "",
          long_perm_footer: r.data.long_perm_footer || "",
        });
        setPlaceholders(r.data.placeholders || []);
      })
      .catch(() => {});
  }, []);
  const save = async () => {
    if (!content.title.trim() || !content.intro.trim()) { toast.error("Title and intro are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/free-preview-content`, content, { withCredentials: true });
      toast.success("Homepage write-up updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="free-preview-content-card">
      <CardHeader>
        <CardTitle className="text-base">Homepage Write-up — &quot;Why This Week&apos;s Draws Are Looking Hot&quot;</CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Edit the title, intro paragraph, and the &quot;Long Permutation&quot; banner/footer that appear around the free picks block. Use placeholders to inject live data:
          {" "}
          {placeholders.map(p => <code key={p} className="bg-gray-100 px-1 mx-0.5 rounded text-[11px]">{p}</code>)}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Title</Label>
          <Input
            value={content.title}
            onChange={e => setContent({ ...content, title: e.target.value })}
            placeholder="Why This Week's Draws Are Looking Hot"
            data-testid="fp-title-input"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-gray-500">Intro paragraph (HTML allowed, e.g. &lt;strong&gt;)</Label>
          <Textarea
            rows={4}
            value={content.intro}
            onChange={e => setContent({ ...content, intro: e.target.value })}
            placeholder="Our expert analysts have studied the Week {week} coupon..."
            className="font-mono text-xs"
            data-testid="fp-intro-input"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Long-Perm Header banner</Label>
            <Input
              value={content.long_perm_header}
              onChange={e => setContent({ ...content, long_perm_header: e.target.value })}
              placeholder="Long Permutation for this Week — Week {week}. Win Big!"
              data-testid="fp-longperm-header-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Long-Perm Footer note</Label>
            <Input
              value={content.long_perm_footer}
              onChange={e => setContent({ ...content, long_perm_footer: e.target.value })}
              placeholder="Perm All — Perm 3 from 10 @ ₦100 = Total ₦12,000 at any pool office."
              data-testid="fp-longperm-footer-input"
            />
          </div>
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={saving} data-testid="fp-save-btn">
          {saving ? "Saving…" : "Save Homepage Write-up"}
        </Button>
      </CardContent>
    </Card>
  );
};

const WonProofsCard = () => {
  const [proofs, setProofs] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", link: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/won-proofs`, { withCredentials: true });
      setProofs(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const create = async () => {
    if (!form.image_url) { toast.error("Upload an image first"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/won-proofs`, form, { withCredentials: true });
      toast.success("Proof added");
      setForm({ title: "", description: "", link: "", image_url: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/won-proofs/${p.proof_id}`, { is_active: !p.is_active }, { withCredentials: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this proof?")) return;
    try {
      await axios.delete(`${API}/admin/won-proofs/${p.proof_id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const activeCount = proofs.filter(p => p.is_active).length;

  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="won-proofs-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Won Proofs — Sitewide Winning Showcase</span>
          <Badge className={`text-xs ${activeCount >= 8 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            {activeCount}/8 active
          </Badge>
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">Upload up to 8 win-proof cards (image + title + description + verify link). They appear on every public page (e.g. &quot;Week 50 won 3/3 — ticket ID&quot; with a confirm link). Any number from 1-8 will render.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Add new */}
        <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-bold text-gray-700">Add a new proof</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase text-gray-500">Title (e.g. &quot;Week 50 — 3/3 Won&quot;)</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Week 50 — 3/3 Won" data-testid="wp-title-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase text-gray-500">Verify Link (optional, e.g. ticket confirm URL)</Label>
              <Input value={form.link} onChange={e => setForm({...form, link: e.target.value})} placeholder="https://..." data-testid="wp-link-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Description (optional)</Label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Ticket #FX1029388 — paid $4,200 / 3 draws hit" data-testid="wp-desc-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-gray-500">Proof image</Label>
            <div className="flex items-center gap-3">
              <Input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="h-9 text-xs" data-testid="wp-image-input" />
              {form.image_url && (
                <img src={form.image_url} alt="preview" className="h-12 w-16 object-cover rounded border border-gray-200" data-testid="wp-image-preview" />
              )}
            </div>
            {uploading && <p className="text-xs text-gray-400">Uploading…</p>}
          </div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={create} disabled={saving || !form.image_url} data-testid="wp-save-btn">
            {saving ? "Saving…" : "Add Proof"}
          </Button>
        </div>

        {/* List */}
        {proofs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No proofs yet — add your first win above.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wp-list">
            {proofs.map(p => (
              <Card key={p.proof_id} className={`border ${p.is_active ? "border-emerald-200 bg-emerald-50/30" : "border-gray-200 bg-gray-50"}`}>
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 space-y-1.5">
                  <p className="font-bold text-sm text-gray-900 leading-tight truncate">{p.title || "—"}</p>
                  {p.description && <p className="text-xs text-gray-500 line-clamp-2">{p.description}</p>}
                  {p.link && <p className="text-[10px] text-blue-600 truncate">{p.link}</p>}
                  <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 mt-2">
                    <button onClick={() => toggleActive(p)} className={`text-[11px] font-bold uppercase ${p.is_active ? "text-emerald-700" : "text-gray-400"}`} data-testid={`wp-toggle-${p.proof_id}`}>
                      {p.is_active ? "● Active" : "○ Hidden"}
                    </button>
                    <button onClick={() => remove(p)} className="text-[11px] font-bold text-red-500 hover:text-red-700" data-testid={`wp-delete-${p.proof_id}`}>Delete</button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const WeeklyPromoCardEditor = () => {
  const [p, setP] = useState({ enabled: true, eyebrow: "", headline: "", sub_headline: "", price_text: "", cta_text: "", cta_link: "/subscribe", contact_label: "", phone_number: "", note: "" });
  const [meta, setMeta] = useState({ week_number: null, coupon_color: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/weekly-promo`).then(r => { setP(p2 => ({ ...p2, ...r.data })); setMeta({ week_number: r.data.week_number, coupon_color: r.data.coupon_color }); }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/weekly-promo`, p, { withCredentials: true });
      toast.success("Weekly promo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="weekly-promo-card">
      <CardHeader>
        <CardTitle className="text-base">Homepage Weekly Promo (Big Bold Card)</CardTitle>
        <p className="text-xs text-gray-500 mt-1">Everything below is editable. Week number + coupon colour are auto-synced from your "Current Coupon" setting on the Packages tab.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-800 flex flex-wrap gap-2 items-center">
          <span className="font-bold">Auto-synced from current coupon:</span>
          <span className="bg-white px-2 py-0.5 rounded border">Week {meta.week_number ?? "?"}</span>
          <span className="bg-white px-2 py-0.5 rounded border capitalize">{meta.coupon_color || "?"} coupon</span>
          <span className="text-gray-500">(edit these from Packages → "Set Current Coupon")</span>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.enabled !== false} onChange={e => setP({ ...p, enabled: e.target.checked })} data-testid="promo-enabled-input" /> Show on homepage</label>
        <div><Label className="text-xs uppercase text-gray-500">Eyebrow (small caps line above)</Label><Input value={p.eyebrow || ""} onChange={e => setP({ ...p, eyebrow: e.target.value })} placeholder="THIS WEEKEND'S FIXED DRAWS" data-testid="promo-eyebrow-input" /></div>
        <div>
          <Label className="text-xs uppercase text-gray-500">Headline (BIG bold — the featured picks)</Label>
          <Input value={p.headline || ""} onChange={e => setP({ ...p, headline: e.target.value })} placeholder="12X  Pairs  43X" className="text-2xl font-black" data-testid="promo-headline-input" />
          <p className="text-[11px] text-gray-500 mt-1">This will be displayed HUGE and BOLD on the homepage.</p>
        </div>
        <div>
          <Label className="text-xs uppercase text-gray-500">Sub-headline</Label>
          <Input value={p.sub_headline || ""} onChange={e => setP({ ...p, sub_headline: e.target.value })} placeholder="+2 Other Bankers" data-testid="promo-sub-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase text-gray-500">Price text</Label><Input value={p.price_text || ""} onChange={e => setP({ ...p, price_text: e.target.value })} placeholder="₦34,000 only" data-testid="promo-price-input" /></div>
          <div><Label className="text-xs uppercase text-gray-500">CTA text</Label><Input value={p.cta_text || ""} onChange={e => setP({ ...p, cta_text: e.target.value })} placeholder="Pay Now to Get the Fixed Draws" data-testid="promo-cta-input" /></div>
          <div><Label className="text-xs uppercase text-gray-500">CTA link</Label><Input value={p.cta_link || ""} onChange={e => setP({ ...p, cta_link: e.target.value })} placeholder="/subscribe" data-testid="promo-cta-link-input" /></div>
          <div><Label className="text-xs uppercase text-gray-500">Contact label</Label><Input value={p.contact_label || ""} onChange={e => setP({ ...p, contact_label: e.target.value })} placeholder="Call Madam Precious" data-testid="promo-contact-label-input" /></div>
          <div><Label className="text-xs uppercase text-gray-500">Phone number</Label><Input value={p.phone_number || ""} onChange={e => setP({ ...p, phone_number: e.target.value })} placeholder="08068833390" data-testid="promo-phone-input" /></div>
        </div>
        <div><Label className="text-xs uppercase text-gray-500">Note / small print</Label><Textarea rows={2} value={p.note || ""} onChange={e => setP({ ...p, note: e.target.value })} placeholder="Bank transfer to First Bank / Access Bank…" data-testid="promo-note-input" /></div>
        <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-weekly-promo-btn">{saving ? "Saving…" : "Save Weekly Promo"}</Button>
      </CardContent>
    </Card>
  );
};

const HeroPitchCard = () => {
  const [pitch, setPitch] = useState({ enabled: true, eyebrow: "", title: "", body: "", bullets: [""], cta_text: "", cta_link: "/subscribe", secondary_cta_text: "", secondary_cta_link: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/hero-pitch`).then(r => setPitch({ ...pitch, ...r.data, bullets: r.data.bullets?.length ? r.data.bullets : [""] })).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...pitch, bullets: (pitch.bullets || []).filter(b => b && b.trim()) };
      await axios.post(`${API}/admin/hero-pitch`, payload, { withCredentials: true });
      toast.success("Hero pitch updated — will show on homepage");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="hero-pitch-card">
      <CardHeader>
        <CardTitle className="text-base">Homepage Persuasive Pitch (Under Hero Banner)</CardTitle>
        <p className="text-xs text-gray-500 mt-1">A conversion-focused write-up that turns visitors into subscribers. Shown right below the hero. Bullets support up to 10 lines.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pitch.enabled !== false} onChange={e => setPitch({ ...pitch, enabled: e.target.checked })} data-testid="hero-pitch-enabled" /> Show on homepage</label>
        <div><Label className="text-xs uppercase text-gray-500">Eyebrow (small caps)</Label><Input value={pitch.eyebrow || ""} onChange={e => setPitch({ ...pitch, eyebrow: e.target.value })} placeholder="WHY SUBSCRIBERS WIN MORE" data-testid="hero-pitch-eyebrow-input" /></div>
        <div><Label className="text-xs uppercase text-gray-500">Title</Label><Input value={pitch.title || ""} onChange={e => setPitch({ ...pitch, title: e.target.value })} placeholder="Stop guessing. Start banking wins…" data-testid="hero-pitch-title-input" /></div>
        <div><Label className="text-xs uppercase text-gray-500">Body (blank line = new paragraph)</Label><Textarea rows={6} value={pitch.body || ""} onChange={e => setPitch({ ...pitch, body: e.target.value })} placeholder="Explain why your service wins…" data-testid="hero-pitch-body-input" /></div>
        <div>
          <Label className="text-xs uppercase text-gray-500">Bullets (one per row)</Label>
          {(pitch.bullets || [""]).map((b, i) => (
            <div key={i} className="flex gap-1 mt-1">
              <Input value={b} onChange={e => { const arr = [...pitch.bullets]; arr[i] = e.target.value; setPitch({ ...pitch, bullets: arr }); }} placeholder="e.g. 70%+ hit rate" />
              <Button size="sm" variant="ghost" type="button" onClick={() => { const arr = pitch.bullets.filter((_, ix) => ix !== i); setPitch({ ...pitch, bullets: arr.length ? arr : [""] }); }}><X className="h-3 w-3" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" type="button" className="mt-1" onClick={() => setPitch({ ...pitch, bullets: [...(pitch.bullets || []), ""] })}>+ Add bullet</Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase text-gray-500">Primary CTA text</Label><Input value={pitch.cta_text || ""} onChange={e => setPitch({ ...pitch, cta_text: e.target.value })} placeholder="See all plans" data-testid="hero-pitch-cta-text" /></div>
          <div><Label className="text-xs uppercase text-gray-500">Primary CTA link</Label><Input value={pitch.cta_link || ""} onChange={e => setPitch({ ...pitch, cta_link: e.target.value })} placeholder="/subscribe" data-testid="hero-pitch-cta-link" /></div>
          <div><Label className="text-xs uppercase text-gray-500">Secondary CTA text</Label><Input value={pitch.secondary_cta_text || ""} onChange={e => setPitch({ ...pitch, secondary_cta_text: e.target.value })} placeholder="View won proofs" /></div>
          <div><Label className="text-xs uppercase text-gray-500">Secondary CTA link</Label><Input value={pitch.secondary_cta_link || ""} onChange={e => setPitch({ ...pitch, secondary_cta_link: e.target.value })} placeholder="/#won-proofs" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-hero-pitch-btn">{saving ? "Saving…" : "Save Pitch"}</Button>
      </CardContent>
    </Card>
  );
};

const CongratsBannerCard = () => {
  const [c, setC] = useState({ enabled: true, title: "", message: "", cta_text: "", cta_link: "/subscribe", highlight: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/congrats-banner`).then(r => setC({ ...c, ...r.data })).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/congrats-banner`, c, { withCredentials: true });
      toast.success("Congratulations banner updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="congrats-banner-card">
      <CardHeader>
        <CardTitle className="text-base">Homepage Congratulations Banner</CardTitle>
        <p className="text-xs text-gray-500 mt-1">A sweet celebration section on the homepage (between hero and weekly promo). Toggle on/off anytime.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.enabled !== false} onChange={e => setC({ ...c, enabled: e.target.checked })} data-testid="congrats-banner-enabled" /> Show on homepage</label>
        <div><Label className="text-xs uppercase text-gray-500">Highlight pill (small caps)</Label><Input value={c.highlight || ""} onChange={e => setC({ ...c, highlight: e.target.value })} placeholder="₦2.4M PAID OUT LAST WEEKEND" data-testid="congrats-banner-highlight" /></div>
        <div><Label className="text-xs uppercase text-gray-500">Title</Label><Input value={c.title || ""} onChange={e => setC({ ...c, title: e.target.value })} placeholder="CONGRATULATIONS TO OUR WINNERS!" data-testid="congrats-banner-title" /></div>
        <div><Label className="text-xs uppercase text-gray-500">Message (blank line = new paragraph)</Label><Textarea rows={4} value={c.message || ""} onChange={e => setC({ ...c, message: e.target.value })} placeholder="Massive shoutout to every subscriber who cashed out…" data-testid="congrats-banner-message" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase text-gray-500">CTA text</Label><Input value={c.cta_text || ""} onChange={e => setC({ ...c, cta_text: e.target.value })} placeholder="Join the winners" data-testid="congrats-banner-cta-text" /></div>
          <div><Label className="text-xs uppercase text-gray-500">CTA link</Label><Input value={c.cta_link || ""} onChange={e => setC({ ...c, cta_link: e.target.value })} placeholder="/subscribe" data-testid="congrats-banner-cta-link" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-congrats-banner-btn">{saving ? "Saving…" : "Save Congrats Banner"}</Button>
      </CardContent>
    </Card>
  );
};

const CongratsPopupCard = () => {
  const [c, setC] = useState({ enabled: true, title: "", subtitle: "", message: "", cta_text: "", cta_link: "/subscribe", frequency_per_day: 2 });
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const loadAnalytics = async (days = 14) => {
    setAnalyticsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/congrats-popup/analytics`, { params: { days }, withCredentials: true });
      setAnalytics(r.data);
    } catch { /* noop */ }
    finally { setAnalyticsLoading(false); }
  };
  useEffect(() => {
    axios.get(`${API}/congrats-popup`).then(r => setC({ ...c, ...r.data })).catch(() => {});
    loadAnalytics(14);
    // eslint-disable-next-line
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/congrats-popup`, c, { withCredentials: true });
      toast.success("Congratulations popup updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const resetLocal = () => {
    try { localStorage.removeItem("fixedraws_congrats_popup_v1"); toast.success("Local popup counter reset — refresh homepage to preview"); }
    catch { /* noop */ }
  };
  return (
    <Card className="bg-white border-gray-200 mt-8" data-testid="congrats-popup-card">
      <CardHeader>
        <CardTitle className="text-base">Winner Congratulations Popup</CardTitle>
        <p className="text-xs text-gray-500 mt-1">A celebration popup shown to every visitor up to N times per day. Fully editable — toggle on/off anytime.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.enabled !== false} onChange={e => setC({ ...c, enabled: e.target.checked })} data-testid="congrats-popup-enabled" /> Enable popup</label>
        <div><Label className="text-xs uppercase text-gray-500">Title (big headline)</Label><Input value={c.title || ""} onChange={e => setC({ ...c, title: e.target.value })} placeholder="CONGRATULATIONS!" data-testid="congrats-popup-title-input" /></div>
        <div><Label className="text-xs uppercase text-gray-500">Subtitle (amber accent)</Label><Input value={c.subtitle || ""} onChange={e => setC({ ...c, subtitle: e.target.value })} placeholder="You're in the right place to win big" data-testid="congrats-popup-subtitle-input" /></div>
        <div><Label className="text-xs uppercase text-gray-500">Message</Label><Textarea rows={4} value={c.message || ""} onChange={e => setC({ ...c, message: e.target.value })} placeholder="Our latest fixed draws just cashed out…" data-testid="congrats-popup-message-input" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs uppercase text-gray-500">CTA text</Label><Input value={c.cta_text || ""} onChange={e => setC({ ...c, cta_text: e.target.value })} placeholder="Claim my winning picks" data-testid="congrats-popup-cta-text" /></div>
          <div><Label className="text-xs uppercase text-gray-500">CTA link</Label><Input value={c.cta_link || ""} onChange={e => setC({ ...c, cta_link: e.target.value })} placeholder="/subscribe" data-testid="congrats-popup-cta-link" /></div>
          <div><Label className="text-xs uppercase text-gray-500">Times per day</Label><Input type="number" min="0" max="10" value={c.frequency_per_day ?? 2} onChange={e => setC({ ...c, frequency_per_day: Number(e.target.value) })} data-testid="congrats-popup-frequency" /></div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-congrats-popup-btn">{saving ? "Saving…" : "Save Popup"}</Button>
          <Button onClick={resetLocal} variant="outline" size="sm" data-testid="reset-congrats-popup-btn">Reset my local counter</Button>
        </div>

        {/* Analytics */}
        <div className="mt-4 pt-4 border-t border-gray-200" data-testid="congrats-popup-analytics">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-sm font-bold text-gray-800">Popup performance (last {analytics?.range_days || 14} days)</p>
            <div className="flex items-center gap-1">
              {[7, 14, 30].map(n => (
                <Button
                  key={n}
                  size="sm"
                  variant={analytics?.range_days === n ? "default" : "outline"}
                  className={analytics?.range_days === n ? "bg-green-600 hover:bg-green-700 text-white h-7 text-xs px-3" : "h-7 text-xs px-3"}
                  onClick={() => loadAnalytics(n)}
                  data-testid={`analytics-range-${n}`}
                >
                  {n}d
                </Button>
              ))}
            </div>
          </div>
          {analyticsLoading ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : analytics ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Popup opens</p>
                  <p className="text-xl font-extrabold text-emerald-900" data-testid="analytics-popup-opens">{analytics.totals.popup_open}</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">Popup CTA clicks</p>
                  <p className="text-xl font-extrabold text-amber-900" data-testid="analytics-popup-clicks">{analytics.totals.popup_click}</p>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-blue-700 font-bold">Conversion rate</p>
                  <p className="text-xl font-extrabold text-blue-900" data-testid="analytics-conversion-rate">{analytics.conversion_rate_pct}%</p>
                </div>
                <div className="rounded-lg bg-pink-50 border border-pink-200 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-pink-700 font-bold">Banner clicks</p>
                  <p className="text-xl font-extrabold text-pink-900" data-testid="analytics-banner-clicks">{analytics.totals.banner_click}</p>
                </div>
              </div>
              {analytics.days.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-2 py-1.5">Day</th>
                        <th className="text-right px-2 py-1.5">Opens</th>
                        <th className="text-right px-2 py-1.5">Clicks</th>
                        <th className="text-right px-2 py-1.5">CTR</th>
                        <th className="text-right px-2 py-1.5">Banner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.days.map(d => {
                        const ctr = d.popup_open ? Math.round(d.popup_click / d.popup_open * 1000) / 10 : 0;
                        return (
                          <tr key={d.day} className="border-t border-gray-100" data-testid={`analytics-row-${d.day}`}>
                            <td className="px-2 py-1.5 font-mono text-gray-700">{d.day}</td>
                            <td className="text-right px-2 py-1.5">{d.popup_open}</td>
                            <td className="text-right px-2 py-1.5">{d.popup_click}</td>
                            <td className="text-right px-2 py-1.5 font-bold text-emerald-700">{ctr}%</td>
                            <td className="text-right px-2 py-1.5 text-pink-700">{d.banner_click}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-400">No events tracked yet.</p>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-400">Analytics unavailable.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [posts, setPosts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [predGames, setPredGames] = useState([]);
  const [wallets, setWallets] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [gamePool, setGamePool] = useState([]);
  const [editingGame, setEditingGame] = useState(null); // {game_id, ...fields}
  const [assignments, setAssignments] = useState([]);
  const [currentCoupon, setCurrentCoupon] = useState({ week_number: 1, coupon_color: "blue", season: "UK" });
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [releases, setReleases] = useState([]);
  const [planDialog, setPlanDialog] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ name: "", tier: "weekly", duration_days: 7, price: 10, features: [""], bet_types: ["Draw"], is_membership: false, is_active: true, highlight: false, color: "blue", sort_order: 99 });
  const [releaseForm, setReleaseForm] = useState({ plan_id: "", title: "", week_number: 1, expires_at: "", notes: "", games: [{ home_team: "", away_team: "", prediction: "Draw", odds: "3.0", league: "", kickoff: "", coupon_number: 1 }] });
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [postDialog, setPostDialog] = useState(false);
  const [pkgDialog, setPkgDialog] = useState(false);
  const [fixDialog, setFixDialog] = useState(false);
  const [scoresDialog, setScoresDialog] = useState(false);
  const [gamesDialog, setGamesDialog] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [editPkg, setEditPkg] = useState(null);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [matchScores, setMatchScores] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Purchase filters
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [ticketReplyDraft, setTicketReplyDraft] = useState({});
  // Daily Fixed Games manual entry state
  const [dailyGamesDialog, setDailyGamesDialog] = useState(null); // {purchase_id, package_name, target_odds, games: []}
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState("all");
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  // Forms
  const [postForm, setPostForm] = useState({ title: "", content: "", category: "blog", image_url: "", tags: [], is_published: true, week_number: null, coupon_color: "brown", season: "UK" });
  const [pkgForm, setPkgForm] = useState({ name: "", description: "", price: 0, num_games: "", bet_type: "", odds: "", kick_off_time: "", prices: {}, details: [""], is_active: true });
  const [fixForm, setFixForm] = useState({ week_number: 1, season: "UK", coupon_color: "brown", kick_off_time: "", matches: Array.from({ length: 10 }, () => ({ home_team: "", away_team: "" })) });
  const [gamesForm, setGamesForm] = useState({ package_id: "", games: [{ home_team: "", away_team: "", prediction: "", odds: "", league: "" }] });

  const fetchAll = async () => {
    try {
      const [s, po, pk, pu, fi, pg, w, an, tk, gp, ga, cc, pl, sb, rl] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/posts`, { withCredentials: true }),
        axios.get(`${API}/admin/packages`, { withCredentials: true }),
        axios.get(`${API}/admin/purchases`, { withCredentials: true }),
        axios.get(`${API}/pool/fixtures`, { withCredentials: true }),
        axios.get(`${API}/admin/prediction-games`, { withCredentials: true }),
        axios.get(`${API}/admin/crypto-wallets`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/support/tickets`, { withCredentials: true }),
        axios.get(`${API}/admin/game-pool`, { withCredentials: true }),
        axios.get(`${API}/admin/game-assignments`, { withCredentials: true }),
        axios.get(`${API}/admin/current-coupon`, { withCredentials: true }).catch(() => ({ data: { week_number: 1, coupon_color: "blue", season: "UK" } })),
        axios.get(`${API}/admin/plans`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/subscriptions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/plans/releases`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setPosts(po.data); setPackages(pk.data);
      setPurchases(pu.data); setFixtures(fi.data); setPredGames(pg.data); setWallets(w.data);
      setAnalytics(an.data); setTickets(tk.data); setGamePool(gp.data); setAssignments(ga.data);
      setCurrentCoupon({ week_number: cc.data.week_number || 1, coupon_color: cc.data.coupon_color || "blue", season: cc.data.season || "UK" });
      setPlans(pl.data); setSubscriptions(sb.data); setReleases(rl.data);
    } catch (e) {
      if (e.response?.status === 403) { toast.error("Admin required"); navigate("/"); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  // Post handlers
  const handleSavePost = async (e) => {
    e.preventDefault();
    try {
      if (editPost) {
        await axios.put(`${API}/admin/posts/${editPost.post_id}`, postForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/posts`, postForm, { withCredentials: true });
      }
      toast.success(editPost ? "Post updated" : "Post created");
      setPostDialog(false); setEditPost(null);
      setPostForm({ title: "", content: "", category: "blog", image_url: "", tags: [], is_published: true, week_number: null, coupon_color: "brown", season: "UK" });
      fetchAll();
    } catch { toast.error("Failed to save post"); }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await axios.delete(`${API}/admin/posts/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size is 5MB"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setPostForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  // Package handlers
  const handleSavePkg = async (e) => {
    e.preventDefault();
    const data = { ...pkgForm, details: pkgForm.details.filter(d => d.trim()), prices: pkgForm.prices || {} };
    if (data.num_games === "") delete data.num_games;
    try {
      if (editPkg) {
        await axios.put(`${API}/admin/packages/${editPkg.package_id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/packages`, data, { withCredentials: true });
      }
      toast.success(editPkg ? "Package updated" : "Package created");
      setPkgDialog(false); setEditPkg(null);
      setPkgForm({ name: "", description: "", price: 0, num_games: "", bet_type: "", odds: "", kick_off_time: "", prices: {}, details: [""], is_active: true });
      fetchAll();
    } catch { toast.error("Failed to save package"); }
  };

  const handleDeletePkg = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try { await axios.delete(`${API}/admin/packages/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  // Purchase handlers
  const handleApprovePurchase = async (id) => {
    try { await axios.put(`${API}/admin/purchases/${id}/approve`, {}, { withCredentials: true }); toast.success("Approved"); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const handleRejectPurchase = async (id) => {
    try { await axios.put(`${API}/admin/purchases/${id}/reject`, {}, { withCredentials: true }); toast.success("Rejected"); fetchAll(); } catch { toast.error("Failed"); }
  };

  // Fixture handlers
  const handleCreateFixture = async (e) => {
    e.preventDefault();
    const valid = fixForm.matches.filter(m => m.home_team && m.away_team);
    if (valid.length < 5) { toast.error("At least 5 matches"); return; }
    try {
      await axios.post(`${API}/admin/pool/fixtures`, { ...fixForm, kick_off_time: new Date(fixForm.kick_off_time).toISOString(), matches: valid }, { withCredentials: true });
      toast.success("Fixture created"); setFixDialog(false); fetchAll();
    } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openScores = (fix) => {
    setSelectedFixture(fix);
    setMatchScores(fix.matches.map(m => ({ match_num: m.match_num, home_score: m.home_score ?? "", away_score: m.away_score ?? "", home_team: m.home_team, away_team: m.away_team })));
    setScoresDialog(true);
  };

  const handleSaveScores = async () => {
    const scored = matchScores.filter(m => m.home_score !== "" && m.away_score !== "").map(m => ({ match_num: m.match_num, home_score: parseInt(m.home_score), away_score: parseInt(m.away_score) }));
    if (!scored.length) { toast.error("Enter at least one score"); return; }
    try {
      await axios.put(`${API}/admin/pool/fixtures/${selectedFixture.fixture_id}/scores`, { matches: scored }, { withCredentials: true });
      toast.success("Scores saved"); setScoresDialog(false); fetchAll();
    } catch { toast.error("Failed"); }
  };

  const handleDeleteFixture = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await axios.delete(`${API}/admin/pool/fixtures/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  // Prediction games handler
  const handleSaveGames = async (e) => {
    e.preventDefault();
    const valid = gamesForm.games.filter(g => g.home_team && g.away_team && g.prediction);
    if (!valid.length) { toast.error("Add at least one game"); return; }
    try {
      await axios.post(`${API}/admin/prediction-games`, { package_id: gamesForm.package_id, games: valid }, { withCredentials: true });
      toast.success("Games saved"); setGamesDialog(false); fetchAll();
    } catch { toast.error("Failed"); }
  };

  // Wallets handler
  const handleSaveWallets = async (e) => {
    e.preventDefault();
    try { await axios.put(`${API}/admin/crypto-wallets`, wallets, { withCredentials: true }); toast.success("Wallets saved"); } catch { toast.error("Failed"); }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: <BarChart3 className="h-5 w-5" /> },
    { id: "analytics", label: "Analytics", icon: <PieChart className="h-5 w-5" /> },
    { id: "posts", label: "Posts", icon: <FileText className="h-5 w-5" /> },
    { id: "releases", label: "Push Games", icon: <Zap className="h-5 w-5" /> },
    { id: "games", label: "Pred. Games", icon: <Trophy className="h-5 w-5" /> },
    { id: "pool", label: "Pool Fixtures", icon: <Layers className="h-5 w-5" /> },
    { id: "tickets", label: "Tickets", icon: <Headphones className="h-5 w-5" /> },
    { id: "settings", label: "Settings", icon: <Settings className="h-5 w-5" /> },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div></div>;

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 min-h-screen p-4 hidden lg:block">
        <div className="flex items-center space-x-2 mb-8 px-2">
          <Trophy className="h-7 w-7 text-green-600" />
          <span className="text-xl font-bold logo-text">FIXEDRAWS</span>
        </div>
        <nav className="space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors sidebar-link ${tab === t.id ? "active" : "text-gray-600 hover:text-gray-900"}`}
              data-testid={`nav-${t.id}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-8">
          <button onClick={async () => { await logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-lg" data-testid="logout-btn">
            <LogOut className="h-5 w-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 flex overflow-x-auto">
        {tabs.slice(0, 5).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-3 text-center text-xs ${tab === t.id ? "text-green-600 font-bold" : "text-gray-500"}`}>
            <div className="flex flex-col items-center gap-1">{t.icon}<span>{t.label}</span></div>
          </button>
        ))}
      </div>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-8 pb-24 lg:pb-8 overflow-auto">
        {/* Overview */}
        {tab === "overview" && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Posts", value: stats.total_posts, icon: <FileText className="h-5 w-5 text-blue-600" />, bg: "bg-blue-50" },
                { label: "Total Purchases", value: stats.total_purchases, icon: <ShoppingCart className="h-5 w-5 text-green-600" />, bg: "bg-green-50" },
                { label: "Pending Approvals", value: stats.pending_approvals, icon: <Eye className="h-5 w-5 text-amber-600" />, bg: "bg-amber-50" },
                { label: "Revenue", value: `$${(stats.total_revenue || 0).toFixed(2)}`, icon: <DollarSign className="h-5 w-5 text-emerald-600" />, bg: "bg-emerald-50" }
              ].map((s, i) => (
                <Card key={i} className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>{s.icon}</div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{s.value || 0}</p>
                    <p className="text-sm text-gray-500">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Recent purchases (only completed deposits) */}
            <Card className="bg-white border-gray-200">
              <CardHeader><CardTitle className="text-base">Recent Deposits</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Phone</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Package</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Amount</th>
                      <th className="text-center py-3 px-4 text-gray-500 font-medium">Status</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                    </tr></thead>
                    <tbody>
                      {purchases.filter(p => p.status !== "awaiting_payment").slice(0, 5).map(p => (
                        <tr key={p.purchase_id} className="border-b border-gray-50">
                          <td className="py-3 px-4 font-mono text-gray-900">{p.phone}</td>
                          <td className="py-3 px-4 text-gray-800">{p.package_name}</td>
                          <td className="py-3 px-4 text-right font-mono text-gray-900">{p.currency && p.currency !== "USD" ? `${p.currency} ${p.amount?.toLocaleString()}` : `$${p.amount?.toFixed(2)}`}</td>
                          <td className="py-3 px-4 text-center"><Badge className={statusClass(p.status)}>{p.status?.toUpperCase()}</Badge></td>
                          <td className="py-3 px-4 text-right">
                            {p.status === "paid" && (
                              <div className="flex justify-end gap-1">
                                <Button size="sm" className="bg-green-600 text-white h-7 text-xs" onClick={() => handleApprovePurchase(p.purchase_id)}><Check className="h-3 w-3 mr-1" />Approve</Button>
                                <Button size="sm" variant="ghost" className="text-red-500 h-7 text-xs" onClick={() => handleRejectPurchase(p.purchase_id)}><X className="h-3 w-3" /></Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {purchases.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-gray-400">No purchases yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {tab === "analytics" && (
          <div className="space-y-6" data-testid="analytics-tab">
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>

            {/* Revenue Over Time */}
            <Card className="bg-white border-gray-200">
              <CardHeader><CardTitle className="text-base">Revenue Over Time (Last 30 Days)</CardTitle></CardHeader>
              <CardContent>
                {analytics?.daily_revenue?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.daily_revenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} />
                      <Bar dataKey="revenue" fill="#16a34a" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-gray-400 py-12">No revenue data yet</p>}
              </CardContent>
            </Card>

            <div className="grid sm:grid-cols-2 gap-6">
              {/* Status Breakdown */}
              <Card className="bg-white border-gray-200">
                <CardHeader><CardTitle className="text-base">Purchase Status</CardTitle></CardHeader>
                <CardContent>
                  {analytics?.status_breakdown && Object.keys(analytics.status_breakdown).length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <RePieChart>
                        <Pie data={Object.entries(analytics.status_breakdown).map(([k, v]) => ({ name: k, value: v }))}
                          cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {Object.keys(analytics.status_breakdown).map((_, i) => (
                            <Cell key={i} fill={["#16a34a","#3b82f6","#f59e0b","#8b5cf6","#94a3b8","#ef4444"][i % 6]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center text-gray-400 py-12">No data</p>}
                </CardContent>
              </Card>

              {/* By Payment Method */}
              <Card className="bg-white border-gray-200">
                <CardHeader><CardTitle className="text-base">Payment Methods</CardTitle></CardHeader>
                <CardContent>
                  {analytics?.by_payment_method?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.by_payment_method} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="method" tick={{ fontSize: 11 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" radius={[0,4,4,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center text-gray-400 py-12">No data</p>}
                </CardContent>
              </Card>

              {/* By Country */}
              <Card className="bg-white border-gray-200">
                <CardHeader><CardTitle className="text-base">Purchases by Country</CardTitle></CardHeader>
                <CardContent>
                  {analytics?.by_country?.length > 0 ? (
                    <div className="space-y-2">
                      {analytics.by_country.map((c, i) => (
                        <div key={c.country} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">{c.country}</span>
                          <div className="flex items-center gap-3">
                            <div className="w-24 bg-gray-100 rounded-full h-2">
                              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(100, (c.count / (analytics.by_country[0]?.count || 1)) * 100)}%` }} />
                            </div>
                            <span className="text-sm text-gray-500 w-12 text-right">{c.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-center text-gray-400 py-12">No data</p>}
                </CardContent>
              </Card>

              {/* By Package */}
              <Card className="bg-white border-gray-200">
                <CardHeader><CardTitle className="text-base">Revenue by Package</CardTitle></CardHeader>
                <CardContent>
                  {analytics?.by_package?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.by_package.map(p => (
                        <div key={p.package} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-700">{p.package || "Unknown"}</p>
                            <p className="text-xs text-gray-500">{p.count} purchase{p.count !== 1 ? "s" : ""}</p>
                          </div>
                          <span className="text-sm font-bold text-green-600">${p.revenue?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-center text-gray-400 py-12">No data</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Posts Tab */}
        {tab === "posts" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
              <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setEditPost(null); setPostForm({ title: "", content: "", category: "blog", image_url: "", tags: [], is_published: true, week_number: null, coupon_color: "brown", season: "UK" }); setPostDialog(true); }} data-testid="create-post-btn">
                <Plus className="h-4 w-4 mr-2" /> New Post
              </Button>
            </div>
            <div className="space-y-3">
              {posts.map(p => (
                <Card key={p.post_id} className="bg-white border-gray-200" data-testid={`admin-post-${p.post_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`cat-${p.category} text-xs`}>{p.category?.replace("_", " ")}</Badge>
                        {!p.is_published && <Badge variant="outline" className="text-xs text-gray-500">Draft</Badge>}
                      </div>
                      <p className="font-semibold text-gray-900">{p.title}</p>
                      <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="text-gray-700" onClick={() => { setEditPost(p); setPostForm({ title: p.title, content: p.content, category: p.category, image_url: p.image_url || "", tags: p.tags || [], is_published: p.is_published, week_number: p.week_number || null, coupon_color: p.coupon_color || "brown", season: p.season || "UK" }); setPostDialog(true); }} data-testid={`edit-post-${p.post_id}`}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeletePost(p.post_id)} data-testid={`delete-post-${p.post_id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {posts.length === 0 && <Card className="bg-white border-gray-200"><CardContent className="py-12 text-center text-gray-400">No posts yet. Create your first post!</CardContent></Card>}
            </div>
            {/* Post Dialog */}
            <Dialog open={postDialog} onOpenChange={setPostDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editPost ? "Edit Post" : "Create Post"}</DialogTitle></DialogHeader>
                <form onSubmit={handleSavePost} className="space-y-4">
                  <div className="space-y-2"><Label>Title</Label><Input value={postForm.title} onChange={e => setPostForm({...postForm, title: e.target.value})} data-testid="post-title-input" /></div>
                  <div className="space-y-2"><Label>Category</Label>
                    <Select value={postForm.category} onValueChange={v => setPostForm({...postForm, category: v})}>
                      <SelectTrigger data-testid="post-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blog">Blog</SelectItem>
                        <SelectItem value="free_pick">Free Pick</SelectItem>
                        <SelectItem value="pool_draws">Pool Draws</SelectItem>
                        <SelectItem value="pool_result">Pool Result</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Week, Colour, Season - shown for pool_draws and pool_result */}
                  {(postForm.category === "pool_draws" || postForm.category === "pool_result") && (
                    <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Week Number</Label>
                        <Select value={String(postForm.week_number || "")} onValueChange={v => setPostForm({...postForm, week_number: parseInt(v)})}>
                          <SelectTrigger data-testid="post-week-select" className="h-9"><SelectValue placeholder="Week" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {Array.from({ length: 52 }, (_, i) => i + 1).map(w => (
                              <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Coupon Colour</Label>
                        <Select value={postForm.coupon_color || "brown"} onValueChange={v => setPostForm({...postForm, coupon_color: v})}>
                          <SelectTrigger data-testid="post-colour-select" className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="brown"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-800 inline-block"></span> Brown</span></SelectItem>
                            <SelectItem value="blue"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-600 inline-block"></span> Blue</span></SelectItem>
                            <SelectItem value="red"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-600 inline-block"></span> Red</span></SelectItem>
                            <SelectItem value="purple"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-purple-600 inline-block"></span> Purple</span></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Season</Label>
                        <Select value={postForm.season || "UK"} onValueChange={v => setPostForm({...postForm, season: v})}>
                          <SelectTrigger data-testid="post-season-select" className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UK">UK Pools</SelectItem>
                            <SelectItem value="Aussie">Aussie Pools</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2"><Label>Content</Label>
                    <div className="admin-quill-editor">
                      <ReactQuill
                        theme="snow"
                        value={postForm.content}
                        onChange={v => setPostForm({...postForm, content: v})}
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ["bold", "italic", "underline", "strike"],
                            [{ list: "ordered" }, { list: "bullet" }],
                            ["blockquote", "link", "image"],
                            [{ align: [] }],
                            ["clean"]
                          ]
                        }}
                        placeholder="Write your post content..."
                        data-testid="post-content-editor"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Post Image</Label>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700 transition-colors">
                        <Upload className="h-4 w-4" />
                        {uploading ? "Uploading..." : "Upload Image"}
                        <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} data-testid="image-upload-input" />
                      </label>
                      {postForm.image_url && (
                        <div className="flex items-center gap-2">
                          <img src={postForm.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${postForm.image_url}` : postForm.image_url} alt="Preview" className="h-10 w-10 rounded object-cover border" />
                          <Button type="button" variant="ghost" size="sm" className="text-red-500 text-xs" onClick={() => setPostForm({...postForm, image_url: ""})} data-testid="remove-image-btn">Remove</Button>
                        </div>
                      )}
                    </div>
                    <Input value={postForm.image_url} onChange={e => setPostForm({...postForm, image_url: e.target.value})} placeholder="Or paste image URL" className="text-xs" />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setPostDialog(false)}>Cancel</Button>
                    <Button type="submit" className="bg-green-600 text-white" data-testid="save-post-btn">{editPost ? "Update" : "Publish"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Packages Tab */}
        {/* Plans Tab (Subscription Plans CRUD, max 8) */}
        {tab === "plans" && (
          <div className="space-y-6" data-testid="plans-tab">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Subscription Plans <span className="text-sm text-gray-500 font-normal">({plans.length}/8)</span></h1>
              <Button
                onClick={() => {
                  if (plans.length >= 8) { toast.error("Max 8 plans allowed. Delete one first."); return; }
                  setEditPlan(null);
                  setPlanForm({ name: "", tier: "weekly", duration_days: 7, price: 10, features: [""], bet_types: ["Draw"], is_membership: false, is_active: true, highlight: false, color: "blue", sort_order: plans.length + 1 });
                  setPlanDialog(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="new-plan-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> New Plan
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((p) => (
                <Card key={p.plan_id} className={`border-2 ${p.highlight ? "border-green-500" : "border-gray-200"}`} data-testid={`admin-plan-${p.plan_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Badge className={`${p.is_membership ? "bg-amber-500" : "bg-blue-600"} text-white text-[10px] mb-1`}>{p.is_membership ? "LIFETIME MEMBERSHIP" : p.tier.toUpperCase()}</Badge>
                        <h3 className="font-bold text-gray-900">{p.name}</h3>
                        <p className="text-2xl font-black text-green-600">${p.price?.toFixed(0)}</p>
                        <p className="text-xs text-gray-500">{p.duration_days == null ? "Lifetime" : `${p.duration_days} days`}</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{p.is_active ? "Active" : "Inactive"}</span>
                        {p.highlight && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">Highlight</span>}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-2 space-y-1">
                      {(p.features || []).slice(0, 4).map((f, i) => <div key={i} className="flex items-start gap-1"><Check className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" /><span>{f}</span></div>)}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(p.bet_types || []).map((b, i) => <span key={i} className="text-[10px] bg-gray-100 rounded px-1.5 py-0.5">{b}</span>)}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setEditPlan(p); setPlanForm({ ...p, features: p.features?.length ? p.features : [""] }); setPlanDialog(true); }} data-testid={`edit-plan-${p.plan_id}`}>
                        <Edit className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={async () => {
                        if (!window.confirm(`Delete "${p.name}"?`)) return;
                        try { await axios.delete(`${API}/admin/plans/${p.plan_id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} data-testid={`delete-plan-${p.plan_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Plan Editor Dialog */}
            <Dialog open={planDialog} onOpenChange={setPlanDialog}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editPlan ? "Edit Plan" : "New Plan"}</DialogTitle>
                  <DialogDescription>Max 8 plans total. Membership = lifetime access + all bet types.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Tier</Label>
                      <Select value={planForm.tier} onValueChange={(v) => {
                        const durMap = { weekly: 7, monthly: 30, quarterly: 90, lifetime: null };
                        setPlanForm({ ...planForm, tier: v, duration_days: durMap[v] ?? planForm.duration_days, is_membership: v === "lifetime" });
                      }}>
                        <SelectTrigger data-testid="plan-tier-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly / Season</SelectItem>
                          <SelectItem value="lifetime">Lifetime (Membership)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Duration (days)</Label>
                      <Input type="number" value={planForm.duration_days ?? ""} placeholder="blank = lifetime" onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value === "" ? null : parseInt(e.target.value) })} data-testid="plan-duration-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Price (USD)</Label>
                      <Input type="number" step="0.01" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: parseFloat(e.target.value) || 0 })} data-testid="plan-price-input" />
                    </div>
                    <div>
                      <Label>Sort Order</Label>
                      <Input type="number" value={planForm.sort_order} onChange={(e) => setPlanForm({ ...planForm, sort_order: parseInt(e.target.value) || 99 })} data-testid="plan-sort-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Features (one per row)</Label>
                    {(planForm.features || [""]).map((f, i) => (
                      <div key={i} className="flex gap-1 mt-1">
                        <Input value={f} onChange={(e) => { const arr = [...planForm.features]; arr[i] = e.target.value; setPlanForm({ ...planForm, features: arr }); }} placeholder="e.g. 30 days access" />
                        <Button size="sm" variant="ghost" type="button" onClick={() => { const arr = planForm.features.filter((_, ix) => ix !== i); setPlanForm({ ...planForm, features: arr.length ? arr : [""] }); }}><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" type="button" className="mt-1" onClick={() => setPlanForm({ ...planForm, features: [...planForm.features, ""] })}>+ Add feature</Button>
                  </div>
                  <div>
                    <Label>Bet Types (comma-separated)</Label>
                    <Input value={(planForm.bet_types || []).join(", ")} onChange={(e) => setPlanForm({ ...planForm, bet_types: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Draw, Correct Score, GG" data-testid="plan-bettypes-input" />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={planForm.is_active} onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })} /> Active</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={planForm.highlight} onChange={(e) => setPlanForm({ ...planForm, highlight: e.target.checked })} /> Highlight (Most Popular)</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={planForm.is_membership} onChange={(e) => setPlanForm({ ...planForm, is_membership: e.target.checked })} /> Membership (lifetime)</label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPlanDialog(false)}>Cancel</Button>
                  <Button className="bg-green-600 text-white" data-testid="save-plan-btn" onClick={async () => {
                    try {
                      const payload = { ...planForm, features: (planForm.features || []).filter((f) => f && f.trim()) };
                      if (editPlan) {
                        await axios.put(`${API}/admin/plans/${editPlan.plan_id}`, payload, { withCredentials: true });
                        toast.success("Plan updated");
                      } else {
                        await axios.post(`${API}/admin/plans`, payload, { withCredentials: true });
                        toast.success("Plan created");
                      }
                      setPlanDialog(false); fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>{editPlan ? "Update" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Subscriptions Tab */}
        {tab === "subscriptions" && (
          <div className="space-y-4" data-testid="subscriptions-tab">
            <h1 className="text-2xl font-bold text-gray-900">Subscriptions <span className="text-sm text-gray-500 font-normal">({subscriptions.length})</span></h1>
            <div className="flex gap-2 flex-wrap">
              {["all", "pending", "active", "awaiting_payment", "expired", "rejected"].map((s) => (
                <Badge key={s} className={`cursor-pointer ${s === "pending" ? "bg-amber-500" : s === "active" ? "bg-green-600" : s === "expired" ? "bg-gray-500" : s === "rejected" ? "bg-red-500" : "bg-gray-300 text-gray-700"} text-white`}>
                  {s} ({s === "all" ? subscriptions.length : subscriptions.filter((x) => x.status === s).length})
                </Badge>
              ))}
            </div>
            <div className="space-y-2">
              {subscriptions.length === 0 && <p className="text-gray-500 text-sm">No subscriptions yet.</p>}
              {subscriptions.map((s) => (
                <Card key={s.subscription_id} className={`border ${s.status === "pending" ? "border-amber-300 bg-amber-50" : s.status === "active" ? "border-green-300 bg-green-50" : "border-gray-200"}`} data-testid={`sub-${s.subscription_id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start flex-wrap gap-3">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${s.status === "active" ? "bg-green-600" : s.status === "pending" ? "bg-amber-500" : s.status === "rejected" ? "bg-red-500" : "bg-gray-400"} text-white text-[10px]`}>{s.status?.toUpperCase()}</Badge>
                          <h3 className="font-bold text-gray-900">{s.plan_name}</h3>
                          <span className="text-xs text-gray-500">${s.amount_usd?.toFixed(2)}</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{s.name} — <span className="font-mono text-xs">{s.email}</span> — {s.phone || "-"}</p>
                        <p className="text-[11px] text-gray-500 mt-1">
                          {new Date(s.created_at).toLocaleString()} • {s.payment_method} {s.transaction_ref && `• ref: ${s.transaction_ref}`}
                        </p>
                        {s.view_code && <p className="mt-1 text-xs">Code: <span className="font-mono bg-white px-2 py-0.5 rounded border border-green-300 font-bold text-green-700">{s.view_code}</span>{s.expires_at && ` — expires ${s.expires_at.slice(0, 10)}`}</p>}
                      </div>
                      <div className="flex flex-col gap-2">
                        {s.screenshot_url && (
                          <Button size="sm" variant="outline" onClick={() => setScreenshotPreview({
                            url: s.screenshot_url.startsWith("http") ? s.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}${s.screenshot_url}`,
                            phone: s.phone || s.email,
                            pkg: s.plan_name,
                            id: s.subscription_id,
                            isSubscription: true,
                          })} data-testid={`view-screenshot-${s.subscription_id}`}>
                            <Eye className="h-3 w-3 mr-1" /> Screenshot
                          </Button>
                        )}
                        {s.status === "pending" && (
                          <div className="flex gap-1">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-sub-${s.subscription_id}`} onClick={async () => {
                              try { const res = await axios.post(`${API}/admin/subscriptions/${s.subscription_id}/approve`, {}, { withCredentials: true }); toast.success(`Approved. Code: ${res.data.view_code}`); fetchAll(); }
                              catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                            }}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                            <Button size="sm" variant="destructive" data-testid={`reject-sub-${s.subscription_id}`} onClick={async () => {
                              const reason = window.prompt("Reject reason (optional):") || "";
                              try { await axios.post(`${API}/admin/subscriptions/${s.subscription_id}/reject`, { reason }, { withCredentials: true }); toast.success("Rejected"); fetchAll(); }
                              catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                            }}><X className="h-3 w-3" /></Button>
                          </div>
                        )}
                        <Button size="sm" variant="ghost" onClick={async () => {
                          if (!window.confirm("Delete this subscription?")) return;
                          try { await axios.delete(`${API}/admin/subscriptions/${s.subscription_id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); }
                          catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }} data-testid={`delete-sub-${s.subscription_id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Push Games (Releases) Tab */}
        {tab === "releases" && (
          <div className="space-y-6" data-testid="releases-tab">
            <h1 className="text-2xl font-bold text-gray-900">Push Games to a Plan</h1>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-5 space-y-3">
                <p className="text-sm text-blue-800">Publish a batch of games for a specific plan. Every active subscriber on that plan gets emailed automatically.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Target Plan</Label>
                    <Select value={releaseForm.plan_id} onValueChange={(v) => setReleaseForm({ ...releaseForm, plan_id: v })}>
                      <SelectTrigger data-testid="release-plan-select"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                      <SelectContent>
                        {plans.filter((p) => p.is_active).map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name} ({subscriptions.filter((s) => s.plan_id === p.plan_id && s.status === "active").length} active)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Release Title</Label>
                    <Input value={releaseForm.title} onChange={(e) => setReleaseForm({ ...releaseForm, title: e.target.value })} placeholder="Week 3 Fixed Draws" data-testid="release-title-input" />
                  </div>
                  <div>
                    <Label>Week Number</Label>
                    <Input type="number" value={releaseForm.week_number} onChange={(e) => setReleaseForm({ ...releaseForm, week_number: parseInt(e.target.value) || 0 })} data-testid="release-week-input" />
                  </div>
                  <div>
                    <Label>Visible until (ISO date, optional)</Label>
                    <Input type="datetime-local" value={releaseForm.expires_at} onChange={(e) => setReleaseForm({ ...releaseForm, expires_at: e.target.value })} data-testid="release-expires-input" />
                  </div>
                </div>
                <div>
                  <Label>Notes to subscribers (optional)</Label>
                  <Textarea rows={2} value={releaseForm.notes} onChange={(e) => setReleaseForm({ ...releaseForm, notes: e.target.value })} placeholder="Focus on the 3/4 Perm. Stake wisely." />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Games ({releaseForm.games.length})</Label>
                    <Button size="sm" variant="outline" type="button" onClick={() => setReleaseForm({ ...releaseForm, games: [...releaseForm.games, { home_team: "", away_team: "", prediction: "Draw", odds: "3.0", league: "", kickoff: "", coupon_number: releaseForm.games.length + 1 }] })} data-testid="add-game-btn">+ Add Game</Button>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {releaseForm.games.map((g, i) => (
                      <div key={i} className="grid grid-cols-12 gap-1 items-center bg-white p-2 rounded border" data-testid={`release-game-row-${i}`}>
                        <Input className="col-span-1" placeholder="#" value={g.coupon_number} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].coupon_number = parseInt(e.target.value) || 0; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Input className="col-span-2" placeholder="Home" value={g.home_team} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].home_team = e.target.value; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Input className="col-span-2" placeholder="Away" value={g.away_team} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].away_team = e.target.value; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Input className="col-span-2" placeholder="Prediction" value={g.prediction} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].prediction = e.target.value; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Input className="col-span-1" placeholder="Odds" value={g.odds} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].odds = e.target.value; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Input className="col-span-2" placeholder="League" value={g.league} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].league = e.target.value; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Input className="col-span-1" placeholder="Kick" value={g.kickoff} onChange={(e) => { const arr = [...releaseForm.games]; arr[i].kickoff = e.target.value; setReleaseForm({ ...releaseForm, games: arr }); }} />
                        <Button size="sm" variant="ghost" className="col-span-1" type="button" onClick={() => setReleaseForm({ ...releaseForm, games: releaseForm.games.filter((_, ix) => ix !== i) })}><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold w-full"
                  data-testid="publish-release-btn"
                  onClick={async () => {
                    if (!releaseForm.plan_id) return toast.error("Choose a plan");
                    if (!releaseForm.title) return toast.error("Add a title");
                    if (releaseForm.games.length === 0) return toast.error("Add at least 1 game");
                    try {
                      const payload = {
                        ...releaseForm,
                        expires_at: releaseForm.expires_at ? new Date(releaseForm.expires_at).toISOString() : null,
                      };
                      const res = await axios.post(`${API}/admin/plans/releases`, payload, { withCredentials: true });
                      toast.success(res.data.message);
                      setReleaseForm({ plan_id: "", title: "", week_number: 1, expires_at: "", notes: "", games: [{ home_team: "", away_team: "", prediction: "Draw", odds: "3.0", league: "", kickoff: "", coupon_number: 1 }] });
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                >
                  <Send className="h-4 w-4 mr-2" /> Publish & Notify Subscribers
                </Button>
              </CardContent>
            </Card>

            <div>
              <h2 className="font-bold text-gray-900 mb-2">Recent Releases</h2>
              <div className="space-y-2">
                {releases.length === 0 && <p className="text-gray-500 text-sm">No releases yet.</p>}
                {releases.map((r) => (
                  <Card key={r.release_id} data-testid={`release-item-${r.release_id}`}>
                    <CardContent className="p-3 flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <p className="text-xs text-gray-500">{new Date(r.created_at).toLocaleString()} · {r.plan_name}</p>
                        <p className="font-bold">{r.title}</p>
                        <p className="text-xs text-gray-500">{(r.games || []).length} games {r.expires_at && `• expires ${r.expires_at.slice(0, 16).replace("T", " ")}`}</p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={async () => {
                        if (!window.confirm("Delete this release?")) return;
                        try { await axios.delete(`${API}/admin/plans/releases/${r.release_id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }} data-testid={`delete-release-${r.release_id}`}><Trash2 className="h-3 w-3" /></Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "packages" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Packages</h1>
              <div className="flex gap-2">
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setEditPkg(null); setPkgForm({ name: "", description: "", price: 0, num_games: "", bet_type: "", odds: "", kick_off_time: "", prices: {}, details: [""], is_active: true }); setPkgDialog(true); }} data-testid="create-package-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Package
                </Button>
              </div>
            </div>

            {/* Set Current Coupon — single source of truth for week/coupon shown sitewide */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-1">Set Current Coupon (Sitewide)</h3>
                <p className="text-xs text-gray-500 mb-3">This controls the week number, coupon color, and season shown on the Buy Draws header, package badges, pool fixtures forecast, AI insights, and free preview. All surfaces sync to whatever you set here.</p>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Week Number</Label>
                    <Input key={`wk-${currentCoupon.week_number}`} type="number" id="cc-week" className="h-10 w-28" placeholder="1" min={1} max={60} defaultValue={currentCoupon.week_number} data-testid="current-coupon-week" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Coupon Color</Label>
                    <select key={`col-${currentCoupon.coupon_color}`} id="cc-color" className="h-10 px-3 border border-gray-300 rounded text-sm bg-white" defaultValue={currentCoupon.coupon_color} data-testid="current-coupon-color">
                      <option value="blue">Blue</option>
                      <option value="red">Red</option>
                      <option value="green">Green</option>
                      <option value="purple">Purple</option>
                      <option value="yellow">Yellow</option>
                      <option value="orange">Orange</option>
                      <option value="brown">Brown</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Season</Label>
                    <select key={`se-${currentCoupon.season}`} id="cc-season" className="h-10 px-3 border border-gray-300 rounded text-sm bg-white" defaultValue={currentCoupon.season} data-testid="current-coupon-season">
                      <option value="UK">UK 2025/26</option>
                      <option value="Australia">Australia 2025/26</option>
                    </select>
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10 px-6" data-testid="save-current-coupon-btn" onClick={async () => {
                    const wk = parseInt(document.getElementById("cc-week").value);
                    const color = document.getElementById("cc-color").value;
                    const season = document.getElementById("cc-season").value;
                    if (!wk || wk < 1) { toast.error("Enter a valid week number"); return; }
                    try {
                      const res = await axios.post(`${API}/admin/current-coupon`, { week_number: wk, coupon_color: color, season }, { withCredentials: true });
                      toast.success(res.data.message);
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>
                    Save Current Coupon
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Auto-Generate Packages */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-2">Package Manager</h3>
                <p className="text-xs text-gray-500 mb-3">Your packages: 4 Correct Scores (1-1, 2-2, 3-3, 0-0) + 5 Draws (1-5) + 4 Perm (3/4, 3/5, 4/5, 3/6) + Aussie Season Key = 14 total. Generate refreshes all packages with new base prices.</p>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Base Price (USD)</Label>
                    <Input type="number" id="pkg-base-price" className="h-10 w-36" placeholder="5" min={1} defaultValue={5} data-testid="pkg-base-price" />
                  </div>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold h-10 px-6" data-testid="auto-gen-packages-btn" onClick={async () => {
                    const bp = parseFloat(document.getElementById("pkg-base-price").value);
                    if (!bp || bp <= 0) { toast.error("Enter a base price"); return; }
                    // Optionally inherit current-coupon inputs (so one click syncs week + packages)
                    const wkEl = document.getElementById("cc-week");
                    const colorEl = document.getElementById("cc-color");
                    const seasonEl = document.getElementById("cc-season");
                    const payload = { base_price: bp };
                    if (wkEl && wkEl.value) payload.week_number = parseInt(wkEl.value);
                    if (colorEl && colorEl.value) payload.coupon_color = colorEl.value;
                    if (seasonEl && seasonEl.value) payload.season = seasonEl.value;
                    try {
                      toast.info("Regenerating 14 packages...");
                      const res = await axios.post(`${API}/admin/regenerate-packages`, payload, { withCredentials: true });
                      toast.success(res.data.message);
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>
                    Regenerate All 14 Packages
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  <span className="bg-white rounded px-2 py-1 text-green-700 border border-green-200 font-bold">CS 1-1 = x1.0</span>
                  <span className="bg-white rounded px-2 py-1 text-green-700 border border-green-200 font-bold">CS 2-2 = x1.0</span>
                  <span className="bg-white rounded px-2 py-1 text-green-700 border border-green-200 font-bold">CS 3-3 = x1.0</span>
                  <span className="bg-white rounded px-2 py-1 text-green-700 border border-green-200 font-bold">CS 0-0 = x1.0</span>
                  <span className="bg-white rounded px-2 py-1 text-blue-700 border border-blue-200">1 Draw = x0.6</span>
                  <span className="bg-white rounded px-2 py-1 text-blue-700 border border-blue-200">2 Draws = x1.0</span>
                  <span className="bg-white rounded px-2 py-1 text-blue-700 border border-blue-200">3 Draws = x1.6</span>
                  <span className="bg-white rounded px-2 py-1 text-blue-700 border border-blue-200">4 Draws = x2.4</span>
                  <span className="bg-white rounded px-2 py-1 text-blue-700 border border-blue-200">5 Draws = x3.6</span>
                  <span className="bg-white rounded px-2 py-1 text-purple-700 border border-purple-200 font-bold">Perm 3/4 = x3.0</span>
                  <span className="bg-white rounded px-2 py-1 text-purple-700 border border-purple-200 font-bold">Perm 3/5 = x3.5</span>
                  <span className="bg-white rounded px-2 py-1 text-purple-700 border border-purple-200 font-bold">Perm 4/5 = x4.0</span>
                  <span className="bg-white rounded px-2 py-1 text-purple-700 border border-purple-200 font-bold">Perm 3/6 = x4.5</span>
                  <span className="bg-white rounded px-2 py-1 text-amber-700 border border-amber-300 font-bold">Aussie = $500</span>
                </div>
              </CardContent>
            </Card>
            <div className="grid sm:grid-cols-2 gap-4">
              {packages.map(p => (
                <Card key={p.package_id} className="bg-white border-gray-200" data-testid={`admin-pkg-${p.package_id}`}>
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-gray-900">{p.name}</h3>
                      <span className="text-2xl font-extrabold text-green-600">${p.price?.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">{p.description}</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {p.num_games && <Badge variant="outline" className="text-xs">{p.num_games} Games</Badge>}
                      {p.bet_type && <Badge variant="outline" className="text-xs">{p.bet_type}</Badge>}
                      {p.odds && <Badge variant="outline" className="text-xs">Odds: {p.odds}</Badge>}
                      {p.kick_off_time && <Badge variant="outline" className="text-xs">{p.kick_off_time}</Badge>}
                    </div>
                    {p.prices && Object.keys(p.prices).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {Object.entries(p.prices).map(([cur, val]) => (
                          <span key={cur} className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">{cur}: {val}</span>
                        ))}
                      </div>
                    )}
                    {p.details?.map((d, i) => <p key={i} className="text-xs text-gray-600 flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />{d}</p>)}
                    <div className="flex gap-1 mt-4">
                      <Button size="sm" variant="outline" className="text-gray-700 border-gray-300" onClick={() => { setEditPkg(p); setPkgForm({ name: p.name, description: p.description, price: p.price, num_games: p.num_games || "", bet_type: p.bet_type || "", odds: p.odds || "", kick_off_time: p.kick_off_time || "", prices: p.prices || {}, details: p.details?.length ? p.details : [""], is_active: p.is_active }); setPkgDialog(true); }}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeletePkg(p.package_id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {packages.length === 0 && <Card className="bg-white border-gray-200 col-span-2"><CardContent className="py-12 text-center text-gray-400">No packages. Create one to start selling!</CardContent></Card>}
            </div>
            {/* Package Dialog */}
            <Dialog open={pkgDialog} onOpenChange={setPkgDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editPkg ? "Edit Package" : "Create Package"}</DialogTitle></DialogHeader>
                <form onSubmit={handleSavePkg} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Name</Label><Input value={pkgForm.name} onChange={e => setPkgForm({...pkgForm, name: e.target.value})} data-testid="pkg-name-input" /></div>
                    <div className="space-y-2"><Label>Price (USD)</Label><Input type="number" step="0.01" value={pkgForm.price} onChange={e => setPkgForm({...pkgForm, price: parseFloat(e.target.value) || 0})} data-testid="pkg-price-input" /></div>
                  </div>
                  <div className="space-y-2"><Label>Description</Label><Input value={pkgForm.description} onChange={e => setPkgForm({...pkgForm, description: e.target.value})} /></div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1"><Label className="text-xs">No. of Games</Label><Input type="number" value={pkgForm.num_games} onChange={e => setPkgForm({...pkgForm, num_games: parseInt(e.target.value) || ""})} data-testid="pkg-num-games" /></div>
                    <div className="space-y-1"><Label className="text-xs">Bet Type</Label><Input value={pkgForm.bet_type} onChange={e => setPkgForm({...pkgForm, bet_type: e.target.value})} placeholder="e.g. Draw" data-testid="pkg-bet-type" /></div>
                    <div className="space-y-1"><Label className="text-xs">Odds</Label><Input value={pkgForm.odds} onChange={e => setPkgForm({...pkgForm, odds: e.target.value})} placeholder="e.g. 5.0" /></div>
                    <div className="space-y-1"><Label className="text-xs">Kick-off Time</Label><Input value={pkgForm.kick_off_time} onChange={e => setPkgForm({...pkgForm, kick_off_time: e.target.value})} placeholder="Sat 3PM" /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Prices in Other Currencies</Label>
                      <Button type="button" variant="outline" size="sm" className="text-xs h-7 text-green-600 border-green-200 hover:bg-green-50"
                        onClick={async () => {
                          if (!pkgForm.price) { toast.error("Enter USD price first"); return; }
                          try {
                            const res = await axios.post(`${API}/convert-prices`, { usd_price: pkgForm.price });
                            setPkgForm({...pkgForm, prices: res.data});
                            toast.success("Prices auto-converted from USD!");
                          } catch { toast.error("Conversion failed"); }
                        }}
                        data-testid="auto-convert-btn"
                      >
                        <DollarSign className="h-3 w-3 mr-1" /> Auto Convert from USD
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {CURRENCIES.filter(c => c.code !== "USD").map(cur => (
                        <div key={cur.code} className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 w-12 shrink-0">{cur.code}</span>
                          <Input type="number" step="0.01" className="h-8 text-sm"
                            value={(pkgForm.prices || {})[cur.code] || ""}
                            onChange={e => {
                              const v = parseFloat(e.target.value);
                              const np = {...(pkgForm.prices || {})};
                              if (v) np[cur.code] = v; else delete np[cur.code];
                              setPkgForm({...pkgForm, prices: np});
                            }}
                            placeholder={cur.symbol}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Details</Label>
                    {pkgForm.details.map((d, i) => (
                      <div key={i} className="flex gap-2">
                        <Input value={d} onChange={e => { const nd = [...pkgForm.details]; nd[i] = e.target.value; setPkgForm({...pkgForm, details: nd}); }} data-testid={`pkg-detail-${i}`} />
                        {i === pkgForm.details.length - 1 && <Button type="button" variant="outline" size="sm" onClick={() => setPkgForm({...pkgForm, details: [...pkgForm.details, ""]})}>+</Button>}
                      </div>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setPkgDialog(false)}>Cancel</Button>
                    <Button type="submit" className="bg-green-600 text-white" data-testid="save-pkg-btn">{editPkg ? "Update" : "Create"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Purchases Tab */}
        {tab === "purchases" && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Purchases & Codes</h1>
              <Button size="sm" variant="outline" className="border-gray-300" onClick={() => {
                window.open(`${API}/admin/export/purchases.csv`, "_blank");
              }} data-testid="export-purchases-csv">Export CSV</Button>
            </div>
            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input value={purchaseSearch} onChange={e => setPurchaseSearch(e.target.value)} placeholder="Search by phone, code, or package..." className="pl-10" data-testid="purchase-search-input" />
              </div>
              <div className="flex gap-1.5">
                {["all", "paid", "approved", "viewed", "expired", "rejected"].map(s => (
                  <Button key={s} size="sm" variant={purchaseStatusFilter === s ? "default" : "outline"}
                    className={purchaseStatusFilter === s ? "bg-green-600 text-white" : ""}
                    onClick={() => setPurchaseStatusFilter(s)} data-testid={`filter-${s}`}>
                    {s === "all" ? "All" : s.replace("_", " ").charAt(0).toUpperCase() + s.replace("_", " ").slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="purchases-table">
                    <thead><tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Phone</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Package</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Code</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Method</th>
                      <th className="text-center py-3 px-4 text-gray-500 font-medium">Proof</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Amount</th>
                      <th className="text-center py-3 px-4 text-gray-500 font-medium">Status</th>
                      <th className="text-center py-3 px-4 text-gray-500 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                    </tr></thead>
                    <tbody>
                      {purchases
                        .filter(p => p.status !== "awaiting_payment")
                        .filter(p => purchaseStatusFilter === "all" || p.status === purchaseStatusFilter)
                        .filter(p => {
                          if (!purchaseSearch) return true;
                          const q = purchaseSearch.toLowerCase();
                          return (p.phone || "").toLowerCase().includes(q)
                            || (p.view_code || "").toLowerCase().includes(q)
                            || (p.package_name || "").toLowerCase().includes(q)
                            || (p.payment_method || "").toLowerCase().includes(q);
                        })
                        .map(p => (
                        <tr key={p.purchase_id} className="border-b border-gray-50" data-testid={`purchase-${p.purchase_id}`}>
                          <td className="py-3 px-4 font-mono text-sm text-gray-900">{p.phone}</td>
                          <td className="py-3 px-4 text-xs text-gray-700 max-w-[140px] truncate">{p.email || <span className="text-gray-400">—</span>}</td>
                          <td className="py-3 px-4 text-gray-800">{p.package_name}</td>
                          <td className="py-3 px-4 font-mono text-xs text-gray-800">{p.view_code || "—"}</td>
                          <td className="py-3 px-4 text-xs text-gray-700">{p.payment_method || "—"}</td>
                          <td className="py-3 px-4 text-center">
                            {p.screenshot_url ? (
                              <button onClick={() => setScreenshotPreview({ url: `${process.env.REACT_APP_BACKEND_URL}/api/screenshot/${p.screenshot_url.split('/').pop()}`, phone: p.phone, pkg: p.package_name, id: p.purchase_id })} className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 text-xs font-medium cursor-pointer hover:underline" data-testid={`screenshot-${p.purchase_id}`}>
                                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/screenshot/${p.screenshot_url.split('/').pop()}`} alt="Proof" className="w-8 h-8 object-cover rounded border border-gray-200" onError={(e) => { e.target.src = `${process.env.REACT_APP_BACKEND_URL}${p.screenshot_url}`; }} />
                                View
                              </button>
                            ) : <span className="text-xs text-gray-400">—</span>}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-gray-900">{p.currency && p.currency !== "USD" ? `${p.currency} ${p.amount?.toLocaleString()}` : `$${p.amount?.toFixed(2)}`}</td>
                          <td className="py-3 px-4 text-center"><Badge className={statusClass(p.status)}>{p.status?.toUpperCase()}</Badge></td>
                          <td className="py-3 px-4 text-center" data-testid={`email-cell-${p.purchase_id}`}>
                            {p.email_status === "sent" ? (
                              <div className="inline-flex flex-col items-center gap-0.5">
                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">SENT</Badge>
                                <span className="text-[10px] text-gray-400 uppercase">{p.email_channel?.replace("hostinger_", "host ").replace("_", " ") || ""}</span>
                              </div>
                            ) : p.email_status === "failed" ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200">FAILED</Badge>
                            ) : p.email_status === "skipped" ? (
                              <Badge className="bg-gray-100 text-gray-600 border-gray-200">SKIPPED</Badge>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-4 text-right">
                            {p.status === "paid" && (
                              <div className="flex justify-end gap-1 flex-wrap">
                                <Button size="sm" className={`h-7 text-xs ${p.games_set || p.daily_games_set ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`} data-testid={`set-games-${p.purchase_id}`} onClick={async () => {
                                  let existing = [];
                                  try {
                                    const r = await axios.get(`${API}/admin/purchases/${p.purchase_id}/games`, { withCredentials: true });
                                    existing = r.data.games || [];
                                  } catch { /* noop */ }
                                  setDailyGamesDialog({
                                    purchase_id: p.purchase_id,
                                    package_name: p.package_name,
                                    target_odds: p.target_odds,
                                    is_daily_game: p.is_daily_game,
                                    already_set: p.games_set || p.daily_games_set,
                                    games: existing.length > 0 ? existing : [{ home_team: "", away_team: "", prediction: "", odds: "", league: "", kickoff: "" }],
                                  });
                                }}>{(p.games_set || p.daily_games_set) ? "Edit Games" : "Set Games"}</Button>
                                <Button size="sm" className="bg-green-600 text-white h-7 text-xs" onClick={() => handleApprovePurchase(p.purchase_id)} data-testid={`approve-${p.purchase_id}`}><Check className="h-3 w-3 mr-1" />Approve</Button>
                                <Button size="sm" variant="ghost" className="text-red-500 h-7 text-xs" onClick={() => handleRejectPurchase(p.purchase_id)} data-testid={`reject-${p.purchase_id}`}><X className="h-3 w-3" /></Button>
                              </div>
                            )}
                            {(p.status === "approved" || p.status === "viewed" || p.status === "expired" || p.status === "rejected" || p.status === "paid") && p.email && (
                              <Button size="sm" variant="outline" className="h-7 text-[10px] border-amber-200 text-amber-700 hover:bg-amber-50 ml-1" data-testid={`resend-email-${p.purchase_id}`} onClick={async () => {
                                try {
                                  toast.info("Re-sending email...");
                                  const r = await axios.post(`${API}/admin/purchases/${p.purchase_id}/resend-email`, {}, { withCredentials: true });
                                  if (r.data.success) toast.success(r.data.message); else toast.error(r.data.message);
                                  fetchAll();
                                } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
                              }}>Resend</Button>
                            )}
                            {(p.status === "viewed" || p.status === "expired") && (
                              <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 h-7 text-xs ml-1" onClick={async () => {
                                try {
                                  const res = await axios.put(`${API}/admin/purchases/${p.purchase_id}/revalidate`, {}, { withCredentials: true });
                                  toast.success(res.data.message);
                                  fetchAll();
                                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                              }} data-testid={`revalidate-${p.purchase_id}`}>Revalidate</Button>
                            )}
                            {p.status === "rejected" && (
                              <Button size="sm" variant="ghost" className="text-red-500 h-7 text-xs ml-1" onClick={async () => {
                                if (!window.confirm("Delete this rejected purchase permanently?")) return;
                                try {
                                  await axios.delete(`${API}/admin/purchases/${p.purchase_id}`, { withCredentials: true });
                                  toast.success("Deleted");
                                  fetchAll();
                                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                              }} data-testid={`delete-purchase-${p.purchase_id}`}>Delete</Button>
                            )}
                            {p.status !== "rejected" && (
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 text-xs ml-1" onClick={async () => {
                                if (!window.confirm(`Permanently delete purchase ${p.purchase_id} for ${p.email}? This cannot be undone.`)) return;
                                try {
                                  await axios.delete(`${API}/admin/purchases/${p.purchase_id}`, { withCredentials: true });
                                  toast.success("Purchase deleted");
                                  fetchAll();
                                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                              }} data-testid={`delete-purchase-${p.purchase_id}`}>Delete</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {purchases.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-gray-400">No purchases yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Screenshot Preview Modal — moved to top-level below so it works from any tab */}
          </div>
        )}

        {/* Prediction Games Tab */}
        {tab === "games" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Game Pool</h1>
              <div className="flex gap-2">
                <Button variant="outline" className="text-red-600 border-red-200" onClick={async () => {
                  if (!confirm("Clear all games from pool?")) return;
                  try { await axios.delete(`${API}/admin/game-pool`, { withCredentials: true }); toast.success("Pool cleared"); fetchAll(); } catch { toast.error("Failed"); }
                }}>Clear Pool</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setGamesForm({ games: [] }); setGamesDialog(true); }} data-testid="add-games-btn">
                  <Plus className="h-4 w-4 mr-2" /> Manual Add
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-500">Pick a date, click Generate — fixtures and predictions are auto-created. Users get random picks from this pool.</p>

            {/* Auto-Generate Section */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-3">Auto-Generate Game Pool</h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Kick-off Date</Label>
                    <Input type="date" id="auto-gen-date" className="h-10 w-48" data-testid="auto-gen-date" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" id="clear-before-gen" className="accent-green-600 h-4 w-4" defaultChecked />
                    Clear existing pool first
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" id="auto-gen-packages" className="accent-amber-500 h-4 w-4" defaultChecked />
                    Also generate packages
                  </label>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Base Price ($)</Label>
                    <Input type="number" id="pool-base-price" className="h-10 w-24" placeholder="10" min={1} defaultValue={10} data-testid="pool-base-price" />
                  </div>
                  <Button className="bg-green-600 hover:bg-green-700 text-white h-10 px-6" data-testid="auto-generate-btn" onClick={async () => {
                    const dateVal = document.getElementById("auto-gen-date").value;
                    if (!dateVal) { toast.error("Pick a date first"); return; }
                    const clear = document.getElementById("clear-before-gen").checked;
                    const autoPackages = document.getElementById("auto-gen-packages").checked;
                    const bp = parseFloat(document.getElementById("pool-base-price").value) || 10;
                    try {
                      toast.info("Fetching fixtures & generating predictions...");
                      const payload = { date: dateVal, clear_existing: clear };
                      if (autoPackages) {
                        payload.auto_generate_packages = true;
                        payload.base_price = bp;
                        payload.kickoff = "Saturday 3PM";
                      }
                      const res = await axios.post(`${API}/admin/auto-generate-pool`, payload, { withCredentials: true });
                      let msg = res.data.message;
                      if (res.data.packages) msg += ` + ${res.data.packages.count} packages created`;
                      toast.success(msg);
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>
                    Generate Predictions
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Fetches fixtures, generates predictions (Draw, Over 2.5, GG, Nap 3/3, Perm 3/4, Perm 3/5, Perm 4/5) with odds. Check &quot;Also generate packages&quot; to create pricing packages automatically.</p>
              </CardContent>
            </Card>

            {/* Auto-Generate Posts Section */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-3">Auto-Generate Posts</h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Date (for results)</Label>
                    <Input type="date" id="post-gen-date" className="h-10 w-44" data-testid="post-gen-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Week #</Label>
                    <Input type="number" id="post-gen-week" className="h-10 w-24" placeholder="42" min={1} max={52} data-testid="post-gen-week" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Colour</Label>
                    <Select defaultValue="brown" onValueChange={v => document.getElementById("post-gen-color").value = v}>
                      <SelectTrigger className="h-10 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="brown">Brown</SelectItem>
                        <SelectItem value="blue">Blue</SelectItem>
                        <SelectItem value="red">Red</SelectItem>
                        <SelectItem value="purple">Purple</SelectItem>
                      </SelectContent>
                    </Select>
                    <input type="hidden" id="post-gen-color" defaultValue="brown" />
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white h-10" onClick={async () => {
                    const d = document.getElementById("post-gen-date").value;
                    const w = document.getElementById("post-gen-week").value;
                    const c = document.getElementById("post-gen-color").value;
                    if (!d || !w) { toast.error("Enter date and week number"); return; }
                    try {
                      toast.info("Generating pool result post...");
                      const res = await axios.post(`${API}/admin/auto-generate-result-post`, { date: d, week_number: parseInt(w), coupon_color: c }, { withCredentials: true });
                      toast.success(res.data.message);
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }} data-testid="gen-result-post-btn">Generate Result Post</Button>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-gray-900 h-10 font-bold" onClick={async () => {
                    const w = document.getElementById("post-gen-week").value;
                    const c = document.getElementById("post-gen-color").value;
                    if (!w) { toast.error("Enter week number"); return; }
                    try {
                      toast.info("Generating pool draws post...");
                      const res = await axios.post(`${API}/admin/auto-generate-draws-post`, { week_number: parseInt(w), coupon_color: c }, { withCredentials: true });
                      toast.success(res.data.message);
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }} data-testid="gen-draws-post-btn">Generate Draws Post</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 font-bold" onClick={async () => {
                    const d = document.getElementById("post-gen-date").value;
                    const w = document.getElementById("post-gen-week").value;
                    if (!d) { toast.error("Enter date"); return; }
                    if (!window.confirm("Notify all users whose assigned games won for this date? Emails will be sent.")) return;
                    try {
                      toast.info("Notifying winners...");
                      const res = await axios.post(`${API}/admin/notify-winners`, { date: d, week_number: parseInt(w || 0), force: true }, { withCredentials: true });
                      toast.success(res.data.message);
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }} data-testid="notify-winners-btn">Notify Winners</Button>
                  <Button variant="outline" className="h-10 border-gray-300" onClick={() => window.open(`${API}/admin/export/predictions.csv`, "_blank")} data-testid="export-predictions-csv">Export Predictions CSV</Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Result Post: fetches draws from ablefast. Draws Post: creates post from current game pool with Nap/Perm selections. Notify Winners: emails users whose assigned games hit the declared result.</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-200"><CardContent className="p-0"><div className="overflow-x-auto">
              <table className="w-full text-sm"><thead><tr className="border-b border-gray-100">
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">Match</th>
                <th className="text-center py-3 px-3 text-gray-500 font-medium text-xs">Prediction</th>
                <th className="text-center py-3 px-3 text-gray-500 font-medium text-xs">Odds</th>
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">League</th>
                <th className="text-center py-3 px-3 text-gray-500 font-medium text-xs">Kick-off</th>
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">Booking</th>
                <th className="text-right py-3 px-3 text-gray-500 font-medium text-xs">Actions</th>
              </tr></thead><tbody>
                {gamePool.map(g => {
                  const isEditing = editingGame?.game_id === g.game_id;
                  if (isEditing) {
                    return (
                      <tr key={g.game_id} className="border-b border-gray-50 bg-amber-50/50" data-testid={`edit-row-${g.game_id}`}>
                        <td className="py-2 px-3"><div className="flex gap-1">
                          <Input className="h-8 text-xs" placeholder="Home" value={editingGame.home_team} onChange={e => setEditingGame({...editingGame, home_team: e.target.value})} />
                          <Input className="h-8 text-xs" placeholder="Away" value={editingGame.away_team} onChange={e => setEditingGame({...editingGame, away_team: e.target.value})} />
                        </div></td>
                        <td className="py-2 px-3">
                          <Select value={editingGame.prediction} onValueChange={v => setEditingGame({...editingGame, prediction: v})}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Draw">Draw</SelectItem>
                              <SelectItem value="Correct Score 0-0">Correct Score 0-0</SelectItem>
                              <SelectItem value="Correct Score 1-1">Correct Score 1-1</SelectItem>
                              <SelectItem value="Correct Score 2-2">Correct Score 2-2</SelectItem>
                              <SelectItem value="Correct Score 3-3">Correct Score 3-3</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-3"><Input className="h-8 text-xs w-16 text-center" type="number" step="0.01" value={editingGame.odds} onChange={e => setEditingGame({...editingGame, odds: e.target.value})} /></td>
                        <td className="py-2 px-3"><Input className="h-8 text-xs" value={editingGame.league || ""} onChange={e => setEditingGame({...editingGame, league: e.target.value})} /></td>
                        <td className="py-2 px-3"><Input className="h-8 text-xs" value={editingGame.kickoff || ""} onChange={e => setEditingGame({...editingGame, kickoff: e.target.value})} /></td>
                        <td className="py-2 px-3 text-[10px] text-gray-400">—</td>
                        <td className="py-2 px-3 text-right"><div className="flex justify-end gap-1">
                          <Button size="sm" className="bg-green-600 text-white h-7 px-2" data-testid={`save-game-${g.game_id}`} onClick={async () => {
                            try {
                              await axios.put(`${API}/admin/game-pool/${editingGame.game_id}`, {
                                home_team: editingGame.home_team,
                                away_team: editingGame.away_team,
                                prediction: editingGame.prediction,
                                odds: parseFloat(editingGame.odds) || 0,
                                league: editingGame.league,
                                kickoff: editingGame.kickoff,
                              }, { withCredentials: true });
                              toast.success("Updated");
                              setEditingGame(null);
                              fetchAll();
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          }}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-500" onClick={() => setEditingGame(null)}>Cancel</Button>
                        </div></td>
                      </tr>
                    );
                  }
                  return (
                  <tr key={g.game_id} className="border-b border-gray-50">
                    <td className="py-2 px-3 text-gray-900 font-medium">{g.home_team} vs {g.away_team}</td>
                    <td className="py-2 px-3 text-center"><Badge className={g.prediction?.startsWith("Nap") ? "bg-red-100 text-red-700" : g.prediction?.startsWith("Perm") ? "bg-purple-100 text-purple-700" : "bg-green-100 text-green-700"}>{g.prediction}</Badge></td>
                    <td className="py-2 px-3 text-center text-gray-700">{g.odds || "—"}</td>
                    <td className="py-2 px-3 text-gray-500 text-xs">{g.league || "—"}</td>
                    <td className="py-2 px-3 text-center text-gray-700 text-xs">{g.kickoff || "—"}</td>
                    <td className="py-2 px-3"><div className="flex flex-wrap gap-1">
                      {g.booking_betking && <span className="text-[10px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">BK:{g.booking_betking}</span>}
                      {g.booking_bet9ja && <span className="text-[10px] bg-green-50 text-green-700 rounded px-1.5 py-0.5">B9:{g.booking_bet9ja}</span>}
                      {g.booking_sportybet && <span className="text-[10px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">SB:{g.booking_sportybet}</span>}
                      {g.booking_1xbet && <span className="text-[10px] bg-purple-50 text-purple-700 rounded px-1.5 py-0.5">1X:{g.booking_1xbet}</span>}
                      {!g.booking_betking && !g.booking_bet9ja && !g.booking_sportybet && !g.booking_1xbet && <span className="text-[10px] text-gray-400">—</span>}
                    </div></td>
                    <td className="py-2 px-3 text-right"><div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" data-testid={`edit-game-${g.game_id}`} onClick={() => setEditingGame({...g, odds: g.odds || ""})}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={async () => {
                        try { await axios.delete(`${API}/admin/game-pool/${g.game_id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
                      }}><Trash2 className="h-3 w-3" /></Button>
                    </div></td>
                  </tr>
                  );
                })}
                {gamePool.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-400">No games in pool. Fetch & add games.</td></tr>}
              </tbody></table>
            </div></CardContent></Card>

            <div className="flex items-center justify-between"><h2 className="text-xl font-bold text-gray-900">User Game Assignments</h2><Badge variant="outline" className="text-gray-600">{assignments.length} records</Badge></div>
            <Card className="bg-white border-gray-200"><CardContent className="p-0"><div className="overflow-x-auto">
              <table className="w-full text-sm"><thead><tr className="border-b border-gray-100">
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">Phone</th>
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">Code</th>
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">Package</th>
                <th className="text-center py-3 px-3 text-gray-500 font-medium text-xs">Games</th>
                <th className="text-left py-3 px-3 text-gray-500 font-medium text-xs">Date</th>
                <th className="text-right py-3 px-3 text-gray-500 font-medium text-xs">Del</th>
              </tr></thead><tbody>
                {assignments.map(a => (
                  <tr key={a.assignment_id} className="border-b border-gray-50">
                    <td className="py-2 px-3 font-mono text-gray-900">{a.phone}</td>
                    <td className="py-2 px-3 font-mono text-xs text-gray-800">{a.view_code}</td>
                    <td className="py-2 px-3 text-gray-700">{a.package_name}</td>
                    <td className="py-2 px-3 text-center"><details className="cursor-pointer"><summary className="text-green-600 font-medium">{a.games?.length || 0} games</summary><div className="mt-2 text-left space-y-1">{a.games?.map((g,i) => <div key={i} className="text-xs text-gray-600">{i+1}. {g.home_team} vs {g.away_team} — <strong>{g.prediction}</strong> ({g.odds})</div>)}</div></details></td>
                    <td className="py-2 px-3 text-xs text-gray-500">{new Date(a.assigned_at).toLocaleDateString()}</td>
                    <td className="py-2 px-3 text-right"><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={async () => {
                      try { await axios.delete(`${API}/admin/game-assignments/${a.assignment_id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
                    }}><Trash2 className="h-3 w-3" /></Button></td>
                  </tr>
                ))}
                {assignments.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-gray-400">No games assigned yet.</td></tr>}
              </tbody></table>
            </div></CardContent></Card>

            <Dialog open={gamesDialog} onOpenChange={setGamesDialog}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Add Games to Pool</DialogTitle><DialogDescription>Fetch from Ablefast, select games, set predictions. Users get random picks from this pool.</DialogDescription></DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2 items-end">
                    <div className="space-y-1 flex-1"><Label className="text-xs">Kick-off Date</Label><Input type="date" id="fetch-date-pool" className="h-9" data-testid="fetch-date" /></div>
                    <Button type="button" className="bg-green-600 text-white h-9" data-testid="fetch-fixtures-btn" onClick={async () => {
                      const dateVal = document.getElementById("fetch-date-pool").value;
                      if (!dateVal) { toast.error("Pick a date"); return; }
                      try { toast.info("Fetching..."); const res = await axios.get(`${API}/fetch-fixtures?date=${dateVal}`, { withCredentials: true }); const f = res.data.fixtures || []; if (!f.length) { toast.error("No fixtures"); return; }
                        setGamesForm({ games: f.map(fx => ({ home_team: fx.home_team, away_team: fx.away_team, prediction: "1X2", odds: "", league: fx.league, kickoff: `${dateVal} ${fx.kickoff}`, date: dateVal, booking_betking: "", booking_bet9ja: "", booking_sportybet: "", booking_1xbet: "", selected: false })) });
                        toast.success(`${f.length} fixtures!`);
                      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                    }}>Fetch</Button>
                  </div>
                  {gamesForm.games.length > 0 && (<>
                    <div className="flex items-center justify-between">
                      <Label>{gamesForm.games.filter(g=>g.selected).length} of {gamesForm.games.length} selected</Label>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => setGamesForm({games: gamesForm.games.map(g=>({...g, selected:true}))})}>All</Button>
                        <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => setGamesForm({games: gamesForm.games.map(g=>({...g, selected:false}))})}>None</Button>
                      </div>
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
                      {gamesForm.games.map((g, i) => (
                        <div key={i} className={`p-3 rounded-lg border ${g.selected ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={g.selected||false} onChange={e=>{const ng=[...gamesForm.games];ng[i]={...ng[i],selected:e.target.checked};setGamesForm({games:ng});}} className="h-4 w-4 accent-green-600" />
                            <span className="text-sm font-medium text-gray-900 flex-1">{g.home_team} vs {g.away_team}</span>
                            <span className="text-xs text-gray-500">{g.league}</span>
                            <span className="text-xs text-gray-400">{g.kickoff}</span>
                          </div>
                          {g.selected && (
                            <div className="mt-2 grid grid-cols-6 gap-2 ml-7">
                              <Select value={g.prediction} onValueChange={v=>{const ng=[...gamesForm.games];ng[i]={...ng[i],prediction:v};setGamesForm({games:ng});}}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1X2">1X2 (Draw)</SelectItem>
                                  <SelectItem value="Home Win">Home Win</SelectItem>
                                  <SelectItem value="Away Win">Away Win</SelectItem>
                                  <SelectItem value="Over 2.5">Over 2.5</SelectItem>
                                  <SelectItem value="Under 2.5">Under 2.5</SelectItem>
                                  <SelectItem value="GG">GG (Both Score)</SelectItem>
                                  <SelectItem value="NG">NG (No GG)</SelectItem>
                                  <SelectItem value="HT/FT">HT/FT</SelectItem>
                                  <SelectItem value="Correct Score">Correct Score</SelectItem>
                                  <SelectItem value="Draw">Draw</SelectItem>
                                  <SelectItem value="Over 1.5">Over 1.5</SelectItem>
                                  <SelectItem value="Under 3.5">Under 3.5</SelectItem>
                                  <SelectItem value="BTTS & Over 2.5">BTTS & O2.5</SelectItem>
                                  <SelectItem value="Double Chance">Double Chance</SelectItem>
                                  <SelectItem value="Nap 3/3">Nap 3/3</SelectItem>
                                  <SelectItem value="Perm 3/4">Perm 3/4</SelectItem>
                                  <SelectItem value="Perm 3/5">Perm 3/5</SelectItem>
                                  <SelectItem value="Perm 4/5">Perm 4/5</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input placeholder="Odds" value={g.odds} className="h-8 text-xs" onChange={e=>{const ng=[...gamesForm.games];ng[i]={...ng[i],odds:e.target.value};setGamesForm({games:ng});}} />
                              <Input placeholder="BetKing" value={g.booking_betking} className="h-8 text-xs" onChange={e=>{const ng=[...gamesForm.games];ng[i]={...ng[i],booking_betking:e.target.value};setGamesForm({games:ng});}} />
                              <Input placeholder="Bet9ja" value={g.booking_bet9ja} className="h-8 text-xs" onChange={e=>{const ng=[...gamesForm.games];ng[i]={...ng[i],booking_bet9ja:e.target.value};setGamesForm({games:ng});}} />
                              <Input placeholder="SportyBet" value={g.booking_sportybet} className="h-8 text-xs" onChange={e=>{const ng=[...gamesForm.games];ng[i]={...ng[i],booking_sportybet:e.target.value};setGamesForm({games:ng});}} />
                              <Input placeholder="1xBet" value={g.booking_1xbet} className="h-8 text-xs" onChange={e=>{const ng=[...gamesForm.games];ng[i]={...ng[i],booking_1xbet:e.target.value};setGamesForm({games:ng});}} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>)}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={()=>setGamesDialog(false)}>Cancel</Button>
                    <Button type="button" className="bg-green-600 text-white" data-testid="save-pool-btn" onClick={async()=>{
                      const sel=gamesForm.games.filter(g=>g.selected);
                      if(!sel.length){toast.error("Select games");return;}
                      try{await axios.post(`${API}/admin/game-pool`,{games:sel.map(g=>({home_team:g.home_team,away_team:g.away_team,prediction:g.prediction,odds:g.odds,league:g.league,kickoff:g.kickoff,date:g.date||"",booking_betking:g.booking_betking,booking_bet9ja:g.booking_bet9ja,booking_sportybet:g.booking_sportybet,booking_1xbet:g.booking_1xbet}))},{withCredentials:true});toast.success(`${sel.length} games added!`);setGamesDialog(false);fetchAll();}catch{toast.error("Failed");}
                    }}>Add {gamesForm.games.filter(g=>g.selected).length} to Pool</Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Pool Fixtures Tab */}
        {tab === "pool" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Pool Fixtures</h1>
              <div className="flex items-center gap-2 flex-wrap">
                <GeneratePoolFixturesButton onDone={fetchAll} />
                <Button
                  variant="outline"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  onClick={async () => {
                    if (!window.confirm("Run AI analysis on all fixtures? Uses Emergent LLM, takes ~1 min for 49 games.")) return;
                    toast.loading("Running AI analyzer…", { id: "ai-run" });
                    try {
                      const r = await axios.post(`${API}/admin/ai-analyze-pool`, {}, { withCredentials: true });
                      toast.success(`AI analyzed ${r.data?.analyzed || 0} fixtures`, { id: "ai-run" });
                    } catch (e) { toast.error(e.response?.data?.detail || "AI analysis failed", { id: "ai-run" }); }
                  }}
                  data-testid="run-ai-analysis-btn"
                >
                  <Zap className="h-4 w-4 mr-2 text-violet-600" /> Run AI Analyzer
                </Button>
                <Button
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={async () => {
                    const keep = window.prompt("Wipe the free-picks archive + old draws/result posts? Enter a week number to KEEP (e.g. 1) or leave blank to clear everything.", "");
                    if (keep === null) return;
                    const body = keep.trim() ? { keep_week: parseInt(keep, 10) } : {};
                    try {
                      const r = await axios.post(`${API}/admin/clear-archive`, body, { withCredentials: true });
                      toast.success(`Cleared archive (${r.data?.archive_deleted || 0}) + posts (${r.data?.posts_deleted || 0})`);
                      fetchAll();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="clear-archive-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2 text-rose-600" /> Clear Archive
                </Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setFixDialog(true)} data-testid="create-fixture-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Fixture
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {fixtures.map(f => (
                <Card key={f.fixture_id} className="bg-white border-gray-200" data-testid={`admin-fix-${f.fixture_id}`}>
                  <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-bold text-gray-900">Week {f.week_number} — {f.season}</p>
                      <p className="text-xs text-gray-600">{f.coupon_color?.toUpperCase()} | {f.matches?.length} matches | {f.total_draws} draws</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={f.status === "completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>{f.status?.toUpperCase()}</Badge>
                      <Button size="sm" variant="outline" onClick={() => openScores(f)} data-testid={`scores-${f.fixture_id}`}><Edit className="h-3 w-3 mr-1" />Scores</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteFixture(f.fixture_id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {fixtures.length === 0 && <Card className="bg-white border-gray-200"><CardContent className="py-12 text-center text-gray-400">No fixtures yet</CardContent></Card>}
            </div>
            {/* Create Fixture Dialog */}
            <Dialog open={fixDialog} onOpenChange={setFixDialog}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Create Pool Fixture</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateFixture} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Week</Label><Input type="number" min={1} value={fixForm.week_number} onChange={e => setFixForm({...fixForm, week_number: parseInt(e.target.value)||1})} /></div>
                    <div className="space-y-2"><Label>Season</Label>
                      <Select value={fixForm.season} onValueChange={v => setFixForm({...fixForm, season: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="UK">UK</SelectItem><SelectItem value="Aussie">Aussie</SelectItem></SelectContent></Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Coupon</Label>
                      <Select value={fixForm.coupon_color} onValueChange={v => setFixForm({...fixForm, coupon_color: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="brown">Brown</SelectItem><SelectItem value="blue">Blue</SelectItem><SelectItem value="purple">Purple</SelectItem><SelectItem value="red">Red</SelectItem></SelectContent></Select>
                    </div>
                    <div className="space-y-2"><Label>Kick Off</Label><Input type="datetime-local" value={fixForm.kick_off_time} onChange={e => setFixForm({...fixForm, kick_off_time: e.target.value})} /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>Matches (min 5)</Label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {fixForm.matches.map((m, i) => (
                        <div key={i} className="grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
                          <span className="text-sm text-gray-500 w-6">{i+1}.</span>
                          <Input placeholder="Home" value={m.home_team} onChange={e => { const nm = [...fixForm.matches]; nm[i] = {...nm[i], home_team: e.target.value}; setFixForm({...fixForm, matches: nm}); }} />
                          <span className="text-xs text-gray-500">vs</span>
                          <Input placeholder="Away" value={m.away_team} onChange={e => { const nm = [...fixForm.matches]; nm[i] = {...nm[i], away_team: e.target.value}; setFixForm({...fixForm, matches: nm}); }} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setFixDialog(false)}>Cancel</Button>
                    <Button type="submit" className="bg-green-600 text-white" data-testid="submit-fixture-btn">Create</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            {/* Scores Dialog */}
            <Dialog open={scoresDialog} onOpenChange={setScoresDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Enter Scores — Week {selectedFixture?.week_number}</DialogTitle><DialogDescription>Draws are auto-detected</DialogDescription></DialogHeader>
                <div className="space-y-2">
                  {matchScores.map((m, i) => (
                    <div key={m.match_num} className="grid grid-cols-[auto_1fr_auto_auto_auto_1fr] gap-2 items-center">
                      <span className="text-sm text-gray-500 w-6">{m.match_num}.</span>
                      <span className="text-sm truncate">{m.home_team}</span>
                      <Input type="number" min={0} className="w-16 text-center" value={m.home_score} onChange={e => { const ns = [...matchScores]; ns[i] = {...ns[i], home_score: e.target.value}; setMatchScores(ns); }} />
                      <Minus className="h-3 w-3 text-gray-400" />
                      <Input type="number" min={0} className="w-16 text-center" value={m.away_score} onChange={e => { const ns = [...matchScores]; ns[i] = {...ns[i], away_score: e.target.value}; setMatchScores(ns); }} />
                      <span className="text-sm truncate">{m.away_team}</span>
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setScoresDialog(false)}>Cancel</Button>
                  <Button className="bg-green-600 text-white" onClick={handleSaveScores} data-testid="save-scores-btn">Save Scores</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Support Tickets Tab */}
        {tab === "tickets" && (
          <div className="space-y-6" data-testid="tickets-tab">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
              <Badge className="bg-amber-100 text-amber-700">{tickets.filter(t => t.status === "open").length} Open</Badge>
            </div>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tickets-table">
                    <thead><tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">ID</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Subject</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Category</th>
                      <th className="text-center py-3 px-4 text-gray-500 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                    </tr></thead>
                    <tbody>
                      {tickets.map(t => {
                        const isOpen = expandedTicket === t.ticket_id;
                        const draft = ticketReplyDraft[t.ticket_id] || "";
                        return (
                        <React.Fragment key={t.ticket_id}>
                        <tr className="border-b border-gray-50" data-testid={`ticket-${t.ticket_id}`}>
                          <td className="py-3 px-4 font-mono text-xs text-gray-800 align-top">
                            <button onClick={() => setExpandedTicket(isOpen ? null : t.ticket_id)} className="text-green-700 hover:underline" data-testid={`toggle-${t.ticket_id}`}>
                              {isOpen ? "▼" : "▶"} {t.ticket_id}
                            </button>
                          </td>
                          <td className="py-3 px-4 align-top">
                            <p className="font-medium text-gray-900">{t.name}</p>
                            <p className="text-xs text-gray-500">{t.email || t.phone || "—"}</p>
                          </td>
                          <td className="py-3 px-4 align-top">
                            <p className="font-medium text-gray-900 line-clamp-1">{t.subject}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{t.message}</p>
                          </td>
                          <td className="py-3 px-4 align-top"><Badge variant="outline" className="text-xs">{t.category}</Badge></td>
                          <td className="py-3 px-4 text-center align-top">
                            <Badge className={t.status === "open" ? "bg-amber-100 text-amber-700" : t.status === "resolved" ? "bg-green-100 text-green-700" : t.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}>
                              {t.status?.replace("_", " ").toUpperCase()}
                            </Badge>
                            {(t.replies || []).length > 0 && (
                              <div className="text-[10px] text-gray-500 mt-1">{(t.replies || []).length} replies</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-500 align-top">{new Date(t.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-4 text-right align-top">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <Button size="sm" className="bg-indigo-500 text-white h-7 text-xs" onClick={() => setExpandedTicket(isOpen ? null : t.ticket_id)} data-testid={`reply-${t.ticket_id}`}>{isOpen ? "Close" : "Reply"}</Button>
                              {t.status === "open" && (
                                <Button size="sm" className="bg-blue-500 text-white h-7 text-xs" onClick={async () => {
                                  try { await axios.put(`${API}/admin/support/tickets/${t.ticket_id}`, { status: "in_progress" }, { withCredentials: true }); toast.success("Marked in progress"); fetchAll(); } catch { toast.error("Failed"); }
                                }} data-testid={`progress-${t.ticket_id}`}>In Progress</Button>
                              )}
                              {(t.status === "open" || t.status === "in_progress") && (
                                <Button size="sm" className="bg-green-600 text-white h-7 text-xs" onClick={async () => {
                                  try { await axios.put(`${API}/admin/support/tickets/${t.ticket_id}`, { status: "resolved" }, { withCredentials: true }); toast.success("Resolved"); fetchAll(); } catch { toast.error("Failed"); }
                                }} data-testid={`resolve-${t.ticket_id}`}><Check className="h-3 w-3 mr-1" />Resolve</Button>
                              )}
                              {t.status !== "closed" && (
                                <Button size="sm" variant="ghost" className="text-gray-400 h-7 text-xs" onClick={async () => {
                                  try { await axios.put(`${API}/admin/support/tickets/${t.ticket_id}`, { status: "closed" }, { withCredentials: true }); toast.success("Closed"); fetchAll(); } catch { toast.error("Failed"); }
                                }} data-testid={`close-${t.ticket_id}`}><X className="h-3 w-3" /></Button>
                              )}
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 h-7 text-xs" onClick={async () => {
                                if (!window.confirm("Delete this ticket permanently?")) return;
                                try { await axios.delete(`${API}/admin/support/tickets/${t.ticket_id}`, { withCredentials: true }); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
                              }} data-testid={`delete-${t.ticket_id}`}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-gray-50/60" data-testid={`thread-${t.ticket_id}`}>
                            <td colSpan={7} className="px-6 py-5">
                              {/* Thread */}
                              <div className="space-y-3 mb-4 max-h-[420px] overflow-y-auto">
                                {/* Original message */}
                                <div className="bg-white border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                                    <span className="font-bold text-gray-700">{t.name} (original)</span>
                                    <span>{new Date(t.created_at).toLocaleString()}</span>
                                  </div>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap select-text" data-testid={`thread-msg-original-${t.ticket_id}`}>{t.message}</p>
                                </div>
                                {/* Reply thread */}
                                {(t.replies || []).map((r, idx) => (
                                  <div key={idx} className={`border rounded-lg p-3 ${r.from === "admin" ? "bg-indigo-50/60 border-indigo-200 ml-6" : r.from === "system" ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200 mr-6"}`}>
                                    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                                      <span className="font-bold">{r.from === "admin" ? "Admin" : r.from === "system" ? "Auto-Reply" : "User"}</span>
                                      <span>{new Date(r.sent_at).toLocaleString()}</span>
                                    </div>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap select-text" data-testid={`thread-msg-${t.ticket_id}-${idx}`}>{r.message}</p>
                                  </div>
                                ))}
                              </div>
                              {/* Reply box */}
                              <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                                <Label className="text-xs text-gray-500">Your reply (will be emailed to {t.email || "the user (no email on file)"})</Label>
                                <Textarea rows={4} value={draft} onChange={e => setTicketReplyDraft({...ticketReplyDraft, [t.ticket_id]: e.target.value})} placeholder="Type your reply..." data-testid={`reply-input-${t.ticket_id}`} />
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" variant="ghost" onClick={() => setTicketReplyDraft({...ticketReplyDraft, [t.ticket_id]: ""})}>Clear</Button>
                                  <Button size="sm" className="bg-green-600 text-white" data-testid={`send-reply-${t.ticket_id}`} onClick={async () => {
                                    if (!draft.trim()) { toast.error("Type a reply first"); return; }
                                    try {
                                      const r = await axios.post(`${API}/admin/support/tickets/${t.ticket_id}/reply`, { message: draft.trim() }, { withCredentials: true });
                                      toast.success(r.data.email_sent ? "Reply sent & emailed" : "Reply saved (no email on file)");
                                      setTicketReplyDraft({...ticketReplyDraft, [t.ticket_id]: ""});
                                      fetchAll();
                                    } catch { toast.error("Failed to send reply"); }
                                  }}><Send className="h-3 w-3 mr-1" />Send Reply</Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                        );
                      })}
                      {tickets.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-400">No support tickets yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {tab === "settings" && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Payment Settings</h1>
            <form onSubmit={handleSaveWallets}>
              <div className="grid sm:grid-cols-2 gap-6">
                <Card className="bg-white border-gray-200 sm:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Crypto Wallets — 5 per coin, randomized per checkout</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">Each wallet supports its own <strong>network</strong> label (e.g. ERC-20, TRC-20, BEP-20) and a <strong>Tag/Memo required</strong> flag. The system randomly picks one of the 5 when a buyer chooses that crypto.</p>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {["btc", "eth", "usdt", "sol", "bnb", "xrp"].map(coin => {
                      const networkPresets = {
                        btc: ["Bitcoin (BTC) — Bitcoin Mainnet", "Bitcoin (BTC) — Lightning Network", "Bitcoin (BTC) — BEP-20 (BSC)", "Bitcoin (BTC) — Liquid Network", "Bitcoin (BTC) — Bitcoin Cash (BCH bridge)"],
                        eth: ["Ethereum (ETH) — ERC-20", "Ethereum (ETH) — Arbitrum One", "Ethereum (ETH) — Optimism", "Ethereum (ETH) — Base", "Ethereum (ETH) — Polygon (Bridged)"],
                        usdt: ["Tether (USDT) — TRC-20 (TRON)", "Tether (USDT) — ERC-20 (Ethereum)", "Tether (USDT) — BEP-20 (BSC)", "Tether (USDT) — Polygon", "Tether (USDT) — Solana (SPL)"],
                        sol: ["Solana (SOL) — SOL Mainnet", "Solana (SOL) — SOL via Phantom", "Solana (SOL) — SOL via Solflare", "Solana (SOL) — SOL via Backpack", "Solana (SOL) — SOL Wormhole bridge"],
                        bnb: ["BNB — BEP-20 (BSC)", "BNB — BEP-2 (Beacon Chain)", "BNB — opBNB", "BNB — BNB Greenfield", "BNB — BNB Smart Chain (Trust Wallet)"],
                        xrp: ["XRP — XRP Ledger (Mainnet)", "XRP — XRPL EVM Sidechain", "XRP — XRP via Binance", "XRP — XRP via Bitstamp", "XRP — XRP via Uphold"],
                      };
                      const tagDefault = coin === "xrp";
                      const list = (wallets[`${coin}_wallets`] || []);
                      const ensureObj = (w, idx) => (typeof w === "string"
                        ? { address: w, network: networkPresets[coin]?.[idx] || "", tag_required: tagDefault, tag_note: "" }
                        : { address: w?.address || "", network: w?.network || networkPresets[coin]?.[idx] || "", tag_required: !!w?.tag_required, tag_note: w?.tag_note || "" });
                      const normalized = Array.from({ length: 5 }, (_, i) => ensureObj(list[i] || {}, i));
                      const updateAt = (idx, patch) => {
                        const next = normalized.map((w, i) => i === idx ? { ...w, ...patch } : w);
                        setWallets({ ...wallets, [`${coin}_wallets`]: next.filter(w => w.address?.trim()) });
                      };
                      return (
                        <div key={coin} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-bold uppercase text-gray-800">{coin}</Label>
                            <span className="text-[10px] text-gray-500">{normalized.filter(w=>w.address?.trim()).length}/5 configured</span>
                          </div>
                          <div className="space-y-2">
                            {normalized.map((w, idx) => (
                              <div key={idx} className="grid sm:grid-cols-12 gap-2 items-start bg-white border border-gray-100 rounded p-2" data-testid={`wallet-row-${coin}-${idx}`}>
                                <div className="sm:col-span-5">
                                  <Label className="text-[10px] text-gray-500">Wallet {idx + 1} Address</Label>
                                  <Input value={w.address} onChange={e => updateAt(idx, { address: e.target.value })} placeholder={`${coin.toUpperCase()} wallet ${idx + 1}`} className="h-8 text-xs font-mono" data-testid={`wallet-addr-${coin}-${idx}`} />
                                </div>
                                <div className="sm:col-span-4">
                                  <Label className="text-[10px] text-gray-500">Network</Label>
                                  <Input list={`net-${coin}`} value={w.network} onChange={e => updateAt(idx, { network: e.target.value })} placeholder={networkPresets[coin]?.[idx] || "Network"} className="h-8 text-xs" data-testid={`wallet-net-${coin}-${idx}`} />
                                  <datalist id={`net-${coin}`}>{(networkPresets[coin] || []).map(n => <option key={n} value={n} />)}</datalist>
                                </div>
                                <div className="sm:col-span-3 space-y-1">
                                  <Label className="text-[10px] text-gray-500">Tag/Memo Required?</Label>
                                  <div className="flex items-center gap-2 h-8">
                                    <input type="checkbox" checked={w.tag_required} onChange={e => updateAt(idx, { tag_required: e.target.checked })} className="h-4 w-4" data-testid={`wallet-tag-${coin}-${idx}`} />
                                    <span className="text-xs text-gray-700">{w.tag_required ? "Required" : "Not required"}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-base">Bank Accounts (Nigeria) — Multi-bank</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">Add multiple Nigerian bank accounts. Customers will see all of them and pick whichever they prefer.</p>
                  </CardHeader>
                  <CardContent className="space-y-3" data-testid="banks-card">
                    {(wallets.banks || []).map((bk, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-2 relative" data-testid={`bank-row-${i}`}>
                        <Input
                          value={bk.bank_name || ""}
                          onChange={e => { const nb=[...(wallets.banks||[])]; nb[i]={...nb[i], bank_name:e.target.value}; setWallets({...wallets, banks:nb}); }}
                          placeholder="Bank name (e.g. Zenith Bank, UBA, GTBank)"
                          data-testid={`bank-name-${i}`}
                        />
                        <Input
                          value={bk.account_name || ""}
                          onChange={e => { const nb=[...(wallets.banks||[])]; nb[i]={...nb[i], account_name:e.target.value}; setWallets({...wallets, banks:nb}); }}
                          placeholder="Account name"
                          data-testid={`bank-account-name-${i}`}
                        />
                        <Input
                          value={bk.account_number || ""}
                          onChange={e => { const nb=[...(wallets.banks||[])]; nb[i]={...nb[i], account_number:e.target.value}; setWallets({...wallets, banks:nb}); }}
                          placeholder="Account number"
                          data-testid={`bank-account-number-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => { const nb=(wallets.banks||[]).filter((_,j)=>j!==i); setWallets({...wallets, banks:nb}); }}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                          data-testid={`bank-delete-${i}`}
                          aria-label="Remove this bank"
                        >×</button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setWallets({ ...wallets, banks: [...(wallets.banks || []), { bank_name: "", account_name: "", account_number: "" }] })}
                      data-testid="add-bank-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add another bank
                    </Button>
                    <details className="text-xs text-gray-500 pt-2">
                      <summary className="cursor-pointer">Legacy single bank (used as fallback)</summary>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <Input placeholder="Bank Name" value={wallets.bank_name || ""} onChange={e => setWallets({...wallets, bank_name: e.target.value})} />
                        <Input placeholder="Account Name" value={wallets.account_name || ""} onChange={e => setWallets({...wallets, account_name: e.target.value})} />
                        <Input placeholder="Account Number" value={wallets.account_number || ""} onChange={e => setWallets({...wallets, account_number: e.target.value})} />
                      </div>
                    </details>
                  </CardContent>
                </Card>
              </div>
              <Button type="submit" className="mt-4 bg-green-600 hover:bg-green-700 text-white" data-testid="save-wallets-btn">Save Payment Settings</Button>
            </form>

            {/* Promotions & Coupons */}
            <PromotionsCard />

            {/* Homepage Hero Banner */}
            <HeroBannerCard />

            {/* Weekly Promo — big bold homepage callout with picks + price */}
            <WeeklyPromoCardEditor />

            {/* Persuasive homepage pitch (below hero banner) */}
            <HeroPitchCard />

            {/* Congratulations banner + popup (admin-editable, toggleable) */}
            <CongratsBannerCard />
            <CongratsPopupCard />

            {/* Homepage "Why This Week's Draws Are Looking Hot" write-up editor */}
            <FreePreviewContentCard />

            {/* Won Proofs — sitewide winning showcase */}
            <WonProofsCard />

            {/* SMTP / Email Diagnostic */}
            <Card className="bg-white border-gray-200 mt-8">
              <CardHeader>
                <CardTitle className="text-base">Season & Country of Play</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Sets the country/flag shown on the homepage, packages page, and locked teasers. Switch as you change game pools.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="season-aus-btn" onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/season-settings`, { season: "Australia" }, { withCredentials: true });
                      toast.success("Switched to Australia 🇦🇺");
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>🇦🇺 Switch to Australia</Button>
                  <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="season-uk-btn" onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/season-settings`, { season: "UK" }, { withCredentials: true });
                      toast.success("Switched to UK 🇬🇧");
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}>🇬🇧 Switch to UK</Button>
                </div>
                <p className="text-[11px] text-gray-500">Tip: switch the season here whenever you change the source of fixtures (UK pools ↔ Australia pools).</p>
              </CardContent>
            </Card>

            {/* SMTP / Email Diagnostic */}
            <Card className="bg-white border-gray-200 mt-8">
              <CardHeader>
                <CardTitle className="text-base">Email Channels Diagnostic</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Tests Hostinger (primary) + Resend (fallback). If Hostinger ever fails, Resend automatically takes over so emails always get delivered.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs text-gray-500">Test recipient (leave blank = admin email)</Label>
                    <Input id="smtp-test-to" placeholder="buyer@example.com" className="h-9 text-sm" data-testid="smtp-test-to" />
                  </div>
                  <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white h-9" data-testid="smtp-test-btn" onClick={async () => {
                    const to = document.getElementById("smtp-test-to")?.value?.trim();
                    toast.info("Running SMTP test...");
                    try {
                      const url = to ? `${API}/admin/smtp-test?to=${encodeURIComponent(to)}` : `${API}/admin/smtp-test`;
                      const r = await axios.get(url, { withCredentials: true });
                      const d = r.data || {};
                      if (d.status === "ok") toast.success(d.message || "Test email sent"); else toast.error(`SMTP error: ${d.error || "unknown"}`);
                      const el = document.getElementById("smtp-diag-out");
                      if (el) el.textContent = JSON.stringify(d, null, 2);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Diagnostic call failed");
                    }
                  }}>Send Test Email</Button>
                  <Button type="button" variant="outline" className="h-9" data-testid="load-email-logs-btn" onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/admin/email-logs?limit=50`, { withCredentials: true });
                      const el = document.getElementById("email-logs-out");
                      if (el) el.textContent = JSON.stringify(r.data, null, 2);
                      toast.success(`Loaded ${r.data.length} recent emails`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to load logs");
                    }
                  }}>View Recent Email Logs</Button>
                </div>
                <pre id="smtp-diag-out" className="text-[11px] bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-gray-700 max-h-64 overflow-auto" data-testid="smtp-diag-out"></pre>
                <pre id="email-logs-out" className="text-[11px] bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-gray-700 max-h-80 overflow-auto" data-testid="email-logs-out"></pre>
              </CardContent>
            </Card>

            {/* Admin Account Settings */}
            <Card className="bg-white border-gray-200 mt-8">
              <CardHeader><CardTitle className="text-base">Admin Account Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target;
                  const newEmail = form.adminEmail.value.trim();
                  const currentPassword = form.currentPassword.value;
                  const newPassword = form.newPassword.value;
                  const confirmPassword = form.confirmPassword.value;
                  if (!currentPassword) { toast.error("Enter current password to save changes"); return; }
                  if (newPassword && newPassword !== confirmPassword) { toast.error("New passwords do not match"); return; }
                  if (newPassword && newPassword.length < 6) { toast.error("New password must be at least 6 characters"); return; }
                  try {
                    const res = await axios.put(`${API}/admin/account`, {
                      current_password: currentPassword,
                      new_email: newEmail || undefined,
                      new_password: newPassword || undefined
                    }, { withCredentials: true });
                    toast.success(res.data.message);
                    form.currentPassword.value = "";
                    form.newPassword.value = "";
                    form.confirmPassword.value = "";
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
                }} data-testid="admin-account-form">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label>Admin Email (for notifications)</Label>
                      <Input name="adminEmail" type="email" defaultValue={user?.email || ""} placeholder="your@email.com" data-testid="admin-email-input" />
                      <p className="text-xs text-gray-500">This email receives deposit notifications and is used to log in</p>
                    </div>
                    <div className="border-t border-gray-100 pt-3 space-y-1">
                      <Label>Current Password <span className="text-red-500">*</span></Label>
                      <Input name="currentPassword" type="password" placeholder="Required to save changes" data-testid="current-password-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>New Password (leave blank to keep current)</Label>
                      <Input name="newPassword" type="password" placeholder="New password (min 6 chars)" data-testid="new-password-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Confirm New Password</Label>
                      <Input name="confirmPassword" type="password" placeholder="Confirm new password" data-testid="confirm-password-input" />
                    </div>
                  </div>
                  <Button type="submit" className="mt-4 bg-gray-900 hover:bg-gray-800 text-white" data-testid="save-account-btn">Update Account</Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Daily Fixed Games — manual game entry dialog */}
      <Dialog open={!!dailyGamesDialog} onOpenChange={(o) => { if (!o) setDailyGamesDialog(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="daily-games-dialog">
          <DialogHeader>
            <DialogTitle>{dailyGamesDialog?.is_daily_game ? "Set Daily Fixed Games" : `Edit Games — ${dailyGamesDialog?.package_name || ""}`}</DialogTitle>
          </DialogHeader>
          {dailyGamesDialog && (() => {
            const games = dailyGamesDialog.games || [];
            const totalOdds = games.reduce((acc, g) => {
              const o = parseFloat(g.odds);
              return acc * (isNaN(o) || o <= 0 ? 1 : o);
            }, 1);
            const targetOdds = parseFloat(dailyGamesDialog.target_odds) || 0;
            const meetsTarget = totalOdds >= targetOdds;
            const updateGame = (idx, patch) => {
              const next = games.map((g, i) => i === idx ? { ...g, ...patch } : g);
              setDailyGamesDialog({ ...dailyGamesDialog, games: next });
            };
            return (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900"><strong>Package:</strong> {dailyGamesDialog.package_name}</p>
                  {targetOdds > 0 && <p className="text-sm text-blue-900"><strong>Target odds the buyer paid for:</strong> {targetOdds}</p>}
                  <p className={`text-base font-bold mt-2 ${meetsTarget ? "text-emerald-700" : (targetOdds > 0 ? "text-red-700" : "text-emerald-700")}`}>
                    Current total odds: {totalOdds.toFixed(2)}{targetOdds > 0 ? (meetsTarget ? " ✓ meets target" : ` (need ${(targetOdds - totalOdds).toFixed(2)} more)`) : ""}
                  </p>
                  {!dailyGamesDialog.is_daily_game && (dailyGamesDialog.package_name?.toLowerCase().includes("3 draws") || dailyGamesDialog.package_name?.toLowerCase().includes("3 correct")) && (
                    <p className="text-xs text-amber-700 mt-2">💡 +2 free <strong>Perm</strong> bonus draws will be auto-added when you save.</p>
                  )}
                </div>

                <div className="space-y-3">
                  {games.map((g, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid={`daily-game-row-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-gray-700">Game {idx + 1}</span>
                        {games.length > 1 && (
                          <Button size="sm" variant="ghost" className="text-red-500 h-7 px-2" data-testid={`remove-daily-game-${idx}`} onClick={() => {
                            const next = games.filter((_, i) => i !== idx);
                            setDailyGamesDialog({ ...dailyGamesDialog, games: next });
                          }}>Remove</Button>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-12 gap-2">
                        <Input className="sm:col-span-3 h-9 text-sm" placeholder="Home Team" value={g.home_team || ""} onChange={e => updateGame(idx, { home_team: e.target.value })} />
                        <Input className="sm:col-span-3 h-9 text-sm" placeholder="Away Team" value={g.away_team || ""} onChange={e => updateGame(idx, { away_team: e.target.value })} />
                        <Input className="sm:col-span-3 h-9 text-sm" placeholder="Prediction (e.g. Over 2.5)" value={g.prediction || ""} onChange={e => updateGame(idx, { prediction: e.target.value })} />
                        <Input className="sm:col-span-1 h-9 text-sm" type="number" step="0.01" placeholder="Odds" value={g.odds || ""} onChange={e => updateGame(idx, { odds: e.target.value })} />
                        <Input className="sm:col-span-2 h-9 text-sm" placeholder="League" value={g.league || ""} onChange={e => updateGame(idx, { league: e.target.value })} />
                      </div>
                      <Input className="mt-2 h-9 text-sm" placeholder="Kickoff (YYYY-MM-DD HH:MM)" value={g.kickoff || ""} onChange={e => updateGame(idx, { kickoff: e.target.value })} />
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button type="button" variant="outline" className="h-9" disabled={games.length >= 10} data-testid="add-daily-game-btn" onClick={() => {
                    setDailyGamesDialog({ ...dailyGamesDialog, games: [...games, { home_team: "", away_team: "", prediction: "", odds: "", league: "", kickoff: "" }] });
                  }}>+ Add Game ({games.length}/10)</Button>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setDailyGamesDialog(null)}>Cancel</Button>
                    <Button type="button" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-daily-games-btn" onClick={async () => {
                      const cleaned = games.filter(g => g.home_team && g.away_team && g.prediction && parseFloat(g.odds) > 0);
                      if (cleaned.length === 0) { toast.error("Add at least one valid game with home, away, prediction & odds."); return; }
                      try {
                        const r = await axios.put(`${API}/admin/purchases/${dailyGamesDialog.purchase_id}/set-games`, { games: cleaned }, { withCredentials: true });
                        toast.success(`${r.data.games_count} games saved (total odds ${r.data.total_odds})${r.data.perm_bonus_added ? " + 2 Perm bonus" : ""}`);
                        setDailyGamesDialog(null);
                        fetchAll();
                      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                    }}>Save Games</Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Screenshot Preview Modal — top-level so it works from any tab */}
      {screenshotPreview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setScreenshotPreview(null)} data-testid="screenshot-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900">Payment Screenshot</h3>
                <p className="text-xs text-gray-500">{screenshotPreview.phone} — {screenshotPreview.pkg}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={screenshotPreview.url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline">Open full size</a>
                <Button size="sm" variant="ghost" onClick={() => setScreenshotPreview(null)}><X className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[70vh] flex items-center justify-center bg-gray-50">
              <img src={screenshotPreview.url} alt="Payment proof" className="max-w-full max-h-[65vh] object-contain rounded-lg border border-gray-200" data-testid="screenshot-preview-image" />
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              {screenshotPreview.isSubscription ? (
                <>
                  <Button size="sm" className="bg-green-600 text-white" onClick={async () => {
                    try { const res = await axios.post(`${API}/admin/subscriptions/${screenshotPreview.id}/approve`, {}, { withCredentials: true }); toast.success(`Approved. Code: ${res.data.view_code}`); setScreenshotPreview(null); fetchAll(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }} data-testid="modal-approve-sub-btn"><Check className="h-3 w-3 mr-1" />Approve</Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                    const reason = window.prompt("Reject reason (optional):") || "";
                    try { await axios.post(`${API}/admin/subscriptions/${screenshotPreview.id}/reject`, { reason }, { withCredentials: true }); toast.success("Rejected"); setScreenshotPreview(null); fetchAll(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }} data-testid="modal-reject-sub-btn"><X className="h-3 w-3 mr-1" />Reject</Button>
                </>
              ) : (
                <>
                  <Button size="sm" className="bg-green-600 text-white" onClick={() => { handleApprovePurchase(screenshotPreview.id); setScreenshotPreview(null); }} data-testid="modal-approve-btn"><Check className="h-3 w-3 mr-1" />Approve</Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { handleRejectPurchase(screenshotPreview.id); setScreenshotPreview(null); }} data-testid="modal-reject-btn"><X className="h-3 w-3 mr-1" />Reject</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
